/**
 * /geometry-calculator — геометрия «по тому, что дано», с чертежом и ходом
 * решения, как в тетради.
 *
 * Калькуляторы фигур в сети — сотни отдельных форм «введите a и h». Здесь
 * фигура одна на плитку, а поля — ВСЕ её величины: человек заполняет то, что
 * дано в его задаче, решатель сам понимает, хватает ли данных, находит
 * остальное, чертит фигуру в масштабе и пишет решение с названиями теорем.
 *
 * Модули (все без DOM, кроме этого):
 *   core/     точные числа, значения, единицы, геометрическое ядро;
 *   solve/    решатель (прямой вывод по формулам + численный запасной путь);
 *   figures/  фигуры и тела: величины, формулы, модели; метод координат;
 *   draw/     чертёж (SVG) — плоские фигуры и тела;
 *   ui/       запись чисел, ход решения, подписи, экспорт, тренажёр.
 * Режим «Построения» (циркуль и линейка) ляжет в construct/ и подключится
 * вкладкой #geoTabConstruct — см. openTab().
 */
import { makeFigure, figureMeta } from './figures/index.js';
import { solve, chainFor, methodsFor } from './solve/engine.js';
import * as V from './core/val.js';
import * as E from './core/exact.js';
import { parseMeasure, parseAngle, evalExpr, LENGTH, setInputDecimal } from './core/units.js';
import { makeFormatter, texToText, subParts, subPlain } from './ui/format.js';
import { renderStep, composeSolution, solutionText, symbols } from './ui/steps.js';
import { makeLabeler, numbers, symbolText } from './ui/labels.js';
import { drawFigure, EXPORT_CSS } from './draw/index.js';
import { fitHeight } from './draw/sheet.js';
import { drawPoints, drawVectors } from './draw/plane.js';
import { solvePoints, solveVectors, lineTex } from './figures/coords.js';
import { valuesFromSides, classify } from './figures/triangle.js';
import { svgToPng, lifePdf } from './ui/export.js';
import { makeProblem, checkAnswer, rng } from './ui/trainer.js';
import { notationFor, notationLocale, readerText } from './ui/notation.js';
import { fill, isolateMath, listJoin } from './ui/i18n.js';
import { stage, ae, resultShown, refused, refusedNow, report, engineReady } from './ui/telemetry.js';
import { parsePoints } from './ui/paste-points.js';

const CFG = window.__GEO_CFG || {};
const I = CFG.i18n || {};
const SLUG = 'geometry-calculator';
const LOCALE = window.__LOCALE || document.documentElement.lang || 'en';
/* Запись читателя: у es решает страна браузера (es-MX — точка, A(1, 2), Letter), см. ui/notation.js. */
const NOTE_LOCALE = notationLocale(LOCALE, (typeof navigator !== 'undefined' && navigator.languages) || []);
const NOTATION = notationFor(NOTE_LOCALE);
/* «1,000» на странице с десятичной точкой — тысяча, а не единица (core/units.js normalizeNumbers). */
setInputDecimal(NOTATION.dec);
const $ = (id) => document.getElementById(id);
const t = (k) => (I[k] != null ? I[k] : k);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
/* «Дано: a = 5; b = 8» — двоеточие и разделитель списка у языка свои (fr « : », « ; »). */
const labelSep = () => t('label_sep');
const listSep = () => t('list_sep');
/**
 * Текст с нижними индексами — в элемент: индекс («h_c», «hₐ») тегом <sub>,
 * подчёркивание человеку не показывается (ui/format.js subParts).
 */
function putRich(el, s) {
    el.textContent = '';
    for (const p of subParts(s)) {
        if (!p.sub) { el.appendChild(document.createTextNode(p.t)); continue; }
        const e = document.createElement('sub');
        e.textContent = p.t;
        el.appendChild(e);
    }
}
const richHtml = (s) => subParts(s).map((p) => (p.sub ? '<sub>' + esc(p.t) + '</sub>' : esc(p.t))).join('');
/** Текст, который собрал код, — в абзац страницы: формулы изолированы, если страница справа налево. */
const setText = (el, s) => putRich(el, isolateMath(s));

const METRIC_FIRST = ['en'].includes(LOCALE) ? ['mm', 'cm', 'dm', 'm', 'km', 'in', 'ft', 'yd'] : ['mm', 'cm', 'dm', 'm', 'km', 'in', 'ft', 'yd'];

const state = {
    tab: 'shapes', group: 'plane', fig: 'triangle', variant: 'general', n: 6,
    inputs: {}, unit: 'cm', angle: 'deg', digits: 2, pi: 'symbol', labels: 'values',
    targets: null, method: null, sol: 0, overlays: {},
    points: [], vec: { a: {}, b: {}, dim3: false },
    allValues: false, res: null, figObj: null, givens: null, drag: null, lastLayout: null, practice: null,
};

/* ── символы и имена ──────────────────────────────────────────────────────── */
/* Обозначения (S/A, P/u/2p, tg/tan…) — запись школы локали, таблица ui/notation.js, не перевод. */
const SYM = NOTATION.sym;
const sym = (k) => (SYM[k] != null ? SYM[k] : null);
/** Имя величины; ключ «фигура:вид» уточняет его для частного случая (катет, основание). */
const qName = (fig, id, variant) => {
    const Q = CFG.qty || {};
    const v = variant === undefined ? (fig === state.fig ? state.variant : null) : variant;
    return (v && Q[fig + ':' + v] && Q[fig + ':' + v][id]) || (Q[fig] || {})[id] || id;
};
const figName = (id) => (CFG.figures || {})[id] || id;
/**
 * Имя фигуры с видом и числом сторон — шаблонами перевода («Треугольник
 * (прямоугольный)», «Правильный многоугольник, n = 6»): скобки, запятая и
 * порядок слов у языка свои. lower — вид строчными внутри фразы задачи.
 */
function figTitle(fig, variant, n, lower) {
    let name = figName(fig);
    const v = variant && variant !== 'general' ? (((CFG.variants || {})[fig] || {})[variant] || '') : '';
    if (v) name = fill(t('fig_variant'), { fig: name, variant: lower ? v.toLocaleLowerCase(LOCALE) : v });
    if (n) name = fill(t('fig_n'), { fig: name, n });
    return name;
}
const ruleName = (n) => (CFG.rules || {})[n] || n;
function symHtml(fig, id) {
    return richHtml(symbolText(fig, id, sym));
}

/* ── формат ───────────────────────────────────────────────────────────────── */
function formatter() {
    const f = makeFormatter({
        locale: NOTE_LOCALE, digits: state.digits, angle: state.angle, unit: state.unit,
        i18n: Object.fromEntries(Object.entries(CFG.units || {}).map(([k, v]) => ['unit_' + k, v])),
    });
    if (state.pi === 'decimal') {
        // «π числом»: точная форма с π не показывается — только десятичная.
        const base = f.value;
        f.value = (v) => {
            if (v && v.e && v.e.t && v.e.t.some((x) => x.k !== 0)) return base({ e: null, n: v.n });
            return base(v);
        };
    }
    return f;
}
const piValue = () => V.piConst(state.pi === '3.14' ? '3.14' : 'symbol');
/* Разделитель координат: A(1; 2), A(1, 2), A(1|2), A(1 ; 2) — запись локали. */
const coordSep = () => NOTATION.coord.text;
const coordTex = () => NOTATION.coord.tex;

/* ── телеметрия: ui/telemetry.js (stage, ae, resultShown, refused) ───────── */
/** Данное задачи коротко для отказа: «a=3;b=4;c=7» — числа, не текст человека. */
const givenText = () => Object.entries(state.inputs).filter(([, v]) => String(v || '').trim()).map(([k, v]) => k + '=' + String(v).trim()).join(';');

/* ── KaTeX ────────────────────────────────────────────────────────────────── */
function tex(el, src) {
    if (window.katex && window.katex.render) {
        try { window.katex.render(src, el, { throwOnError: false, output: 'html', strict: 'ignore' }); return; } catch (_) { /* ниже — текстом */ }
    }
    putRich(el, texToText(src));
}

/* ── фигура: выбор, поля ──────────────────────────────────────────────────── */
function isCoords() { return state.fig === 'coords' || state.fig === 'vectors'; }

function selectGroup(g, opts = {}) {
    state.group = g;
    document.querySelectorAll('[data-geo-group]').forEach((b) => {
        const on = b.getAttribute('data-geo-group') === g;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-geo-fig]').forEach((b) => { b.hidden = b.getAttribute('data-geo-in') !== g; });
    if (!opts.keepFig) {
        const first = document.querySelector('[data-geo-in="' + g + '"]');
        if (first) selectFigure(first.getAttribute('data-geo-fig'));
    }
}

function selectFigure(id, opts = {}) {
    const meta = figureMeta(id);
    if (!meta) return;
    const prev = state.fig;
    state.fig = id;
    if (meta.group !== state.group) selectGroup(meta.group, { keepFig: true });
    document.querySelectorAll('[data-geo-fig]').forEach((b) => {
        const on = b.getAttribute('data-geo-fig') === id;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on && opts.scroll !== false) { try { b.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (_) { /* старый браузер */ } }
    });
    state.variant = opts.variant || (meta.variants ? meta.variants[0] : null);
    if (meta.n) state.n = Math.max(meta.n[0], Math.min(meta.n[1], Number(opts.n) || (prev === id ? state.n : meta.n[2])));
    if (!opts.keepInputs) state.inputs = {};
    state.targets = null;
    state.method = null;
    state.sol = 0;
    state.overlays = {};
    if (id === 'coords' && !state.points.length) state.points = [{ name: 'A', x: '', y: '' }, { name: 'B', x: '', y: '' }];
    buildVariant();
    buildFields();
    buildSamples();
    buildOverlays();
    buildFormulas();
    // Пример при открытии и ссылка — не действие человека: ни воронки, ни «результата».
    if (!opts.auto) {
        if (prev !== id) ae('figure', { fig: id });
        stage('figure_pick', { fig: id });
    }
    refresh(!opts.auto);
}

function buildVariant() {
    const meta = figureMeta(state.fig);
    const box = $('geoVariant');
    box.innerHTML = '';
    const nRow = $('geoNRow');
    if (meta && meta.variants) {
        for (const v of meta.variants) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'tc-seg-b' + (v === state.variant ? ' on' : '');
            b.setAttribute('role', 'radio');
            b.setAttribute('aria-checked', v === state.variant ? 'true' : 'false');
            b.dataset.geoVariant = v;
            b.textContent = ((CFG.variants || {})[state.fig] || {})[v] || v;
            box.appendChild(b);
        }
        box.hidden = false;
    } else box.hidden = true;
    if (meta && meta.n) {
        nRow.hidden = false;
        $('geoN').min = String(meta.n[0]);
        $('geoN').max = String(meta.n[1]);
        $('geoN').value = String(state.n);
        $('geoNLabel').textContent = t(state.fig === 'polygon' ? 'n_label_polygon' : 'n_label_prism');
    } else nRow.hidden = true;
    const h = ((CFG.variantHints || {})[state.fig] || {})[state.variant];
    let hint = h || readerText(state.fig === 'coords' ? t('given_hint_coords') : state.fig === 'vectors' ? t('given_hint_vectors') : t('given_hint'), NOTATION);
    // Условие можно вставить целиком — пример записью локали (ui/paste-points.js раскладывает по полям).
    if (!h && isCoords()) {
        const sep = coordSep();
        const ex = state.fig === 'coords' ? 'A(1' + sep + '2), B(4' + sep + '6)' : 'a(3' + sep + '4), b(4' + sep + '−3)';
        hint += ' ' + fill(t('paste_points_hint'), { ex });
    }
    setText($('geoHint'), hint);
}

/** Поле ввода величины. */
function fieldRow(fig, id, presetV) {
    const q = fig.qty[id];
    const wrap = document.createElement('div');
    wrap.className = 'geo-f';
    const lid = 'geoIn_' + id;
    const name = qName(state.fig, id);
    wrap.innerHTML = '<label class="geo-f-l" for="' + lid + '"><b>' + symHtml(fig, id) + '</b>' + esc(name) + '</label>'
        + '<input class="geo-in" id="' + lid + '" data-q="' + id + '" type="text" autocomplete="off" autocorrect="off" spellcheck="false" autocapitalize="off" inputmode="decimal" enterkeyhint="next">'
        + '<p class="geo-f-err" id="' + lid + 'Err" role="alert" hidden></p>';
    const inp = wrap.querySelector('input');
    inp.setAttribute('aria-describedby', lid + 'Err');
    if (presetV) {
        inp.value = q.kind === 'angle' ? formatter().angle(presetV).text : formatter().value(presetV).text;
        inp.disabled = true;
        inp.classList.add('given');
        inp.title = t('field_preset');
    } else if (state.inputs[id] != null) inp.value = state.inputs[id];
    return wrap;
}

const PRIMARY_GROUPS = new Set(['main', 'sides', 'angles', 'whole', 'surf']);
/** Основная величина фигуры (стороны, углы, площадь…) — видна сразу; прочие — за «Ещё». */
function isPrimaryQty(fig, id) {
    const q = fig.qty[id];
    return PRIMARY_GROUPS.has(q.group) && !q.aux && !(state.fig === 'triangle' && id === 'p');
}
function buildFields() {
    const box = $('geoFields');
    box.innerHTML = '';
    if (state.fig === 'coords') return buildPointFields(box);
    if (state.fig === 'vectors') return buildVectorFields(box);
    const fig = makeFigure(state.fig, { variant: state.variant, n: state.n });
    state.figObj = fig;
    const primary = document.createElement('div');
    primary.className = 'geo-fields';
    const more = document.createElement('details');
    more.className = 'geo-more';
    more.innerHTML = '<summary>' + esc(t('more_fields')) + '</summary>';
    const moreBox = document.createElement('div');
    moreBox.className = 'geo-fields';
    more.appendChild(moreBox);
    let moreOpen = false;
    for (const id of fig.order) {
        const row = fieldRow(fig, id, fig.presets && fig.presets[id]);
        const isPrimary = isPrimaryQty(fig, id);
        (isPrimary ? primary : moreBox).appendChild(row);
        if (!isPrimary && state.inputs[id]) moreOpen = true;
    }
    box.appendChild(primary);
    if (moreBox.children.length) { more.open = moreOpen; box.appendChild(more); }
    box.querySelectorAll('.geo-in').forEach(bindInput);
}

function buildPointFields(box) {
    const wrap = document.createElement('div');
    wrap.className = 'geo-fields';
    state.points.forEach((p, i) => {
        const row = document.createElement('div');
        row.className = 'geo-f geo-pt';
        row.style.gridTemplateColumns = 'auto minmax(0,1fr) minmax(0,1fr) auto';
        row.innerHTML = '<label class="geo-f-l" for="geoPx' + i + '"><b>' + esc(p.name) + '</b></label>'
            + '<input class="geo-in" id="geoPx' + i + '" data-pt="' + i + '" data-c="x" type="text" inputmode="decimal" autocomplete="off" autocorrect="off" spellcheck="false" autocapitalize="off" placeholder="x" aria-label="' + esc(p.name + ' x') + '">'
            + '<input class="geo-in" id="geoPy' + i + '" data-pt="' + i + '" data-c="y" type="text" inputmode="decimal" autocomplete="off" autocorrect="off" spellcheck="false" autocapitalize="off" placeholder="y" aria-label="' + esc(p.name + ' y') + '">'
            + '<button type="button" class="tc-btn sm ghost" data-geo-rmpt="' + i + '" aria-label="' + esc(fill(t('remove_point_aria'), { name: p.name })) + '"' + (state.points.length <= 2 ? ' disabled' : '') + '>×</button>'
            + '<p class="geo-f-err" id="geoPt' + i + 'Err" role="alert" hidden></p>';
        row.querySelector('[data-c=x]').value = p.x;
        row.querySelector('[data-c=y]').value = p.y;
        wrap.appendChild(row);
    });
    box.appendChild(wrap);
    if (state.points.length < 8) {
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'tc-btn sm ghost';
        add.id = 'geoAddPt';
        add.style.marginTop = '8px';
        add.textContent = t('add_point');
        box.appendChild(add);
        add.addEventListener('click', () => {
            const used = new Set(state.points.map((p) => p.name));
            const name = 'ABCDEFGHKLMN'.split('').find((c) => !used.has(c)) || 'P';
            state.points.push({ name, x: '', y: '' });
            buildFields();
            const last = $('geoPx' + (state.points.length - 1));
            if (last) last.focus();
            refresh(true);
        });
    }
    box.querySelectorAll('.geo-in').forEach(bindInput);
    box.querySelectorAll('[data-geo-rmpt]').forEach((b) => b.addEventListener('click', () => {
        state.points.splice(Number(b.getAttribute('data-geo-rmpt')), 1);
        buildFields();
        refresh(true);
    }));
}

function buildVectorFields(box) {
    const wrap = document.createElement('div');
    wrap.className = 'geo-fields';
    for (const v of ['a', 'b']) {
        const row = document.createElement('div');
        row.className = 'geo-f geo-pt';
        const cols = state.vec.dim3 ? 3 : 2;
        row.style.gridTemplateColumns = 'auto' + ' minmax(0,1fr)'.repeat(cols);
        let html = '<label class="geo-f-l" for="geoV' + v + 'x"><b>' + esc(v) + '</b></label>';
        for (const c of ['x', 'y', 'z'].slice(0, cols)) {
            html += '<input class="geo-in" id="geoV' + v + c + '" data-vec="' + v + '" data-c="' + c + '" type="text" inputmode="decimal" autocomplete="off" autocorrect="off" spellcheck="false" autocapitalize="off" placeholder="' + c + '" aria-label="' + esc(t(v === 'a' ? 'vector_a' : 'vector_b') + ' ' + c) + '">';
        }
        html += '<p class="geo-f-err" id="geoV' + v + 'Err" role="alert" hidden></p>';
        row.innerHTML = html;
        row.querySelectorAll('input').forEach((inp) => { inp.value = state.vec[v][inp.dataset.c] || ''; });
        wrap.appendChild(row);
    }
    box.appendChild(wrap);
    const lab = document.createElement('label');
    lab.className = 'geo-nrow';
    lab.style.marginTop = '8px';
    lab.innerHTML = '<input type="checkbox" id="geoDim3"' + (state.vec.dim3 ? ' checked' : '') + '> ' + esc(readerText(t('dim3'), NOTATION));
    box.appendChild(lab);
    lab.querySelector('input').addEventListener('change', (e) => { state.vec.dim3 = e.target.checked; buildFields(); refresh(true); });
    box.querySelectorAll('.geo-in').forEach(bindInput);
}

let lastField = null;
let typeTimer = 0;
function bindInput(inp) {
    inp.addEventListener('focus', () => { lastField = inp; });
    inp.addEventListener('input', () => {
        if ((inp.dataset.pt != null || inp.dataset.vec) && spreadPoints(inp)) return;
        storeInput(inp);
        clearTimeout(typeTimer);
        typeTimer = setTimeout(() => refresh(true), 260);
    });
    inp.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        clearTimeout(typeTimer);
        refresh(true);
        const all = Array.from(document.querySelectorAll('#geoFields .geo-in:not(:disabled)'));
        const i = all.indexOf(inp);
        if (i >= 0 && all[i + 1]) all[i + 1].focus();
    });
}
/**
 * «A(−2; 3), B(4; −1)» или «(3; 4)», вставленные в поле координаты, — по полям:
 * несколько точек — строками с их именами, одна — в x и y (z) этой строки
 * (ui/paste-points.js). Возвращает true, если запись разложена.
 */
function spreadPoints(inp) {
    const pts = parsePoints(inp.value, NOTATION.dec);
    if (!pts) return false;
    const focusOn = (id) => { const el = $(id); if (el) { el.focus(); try { el.setSelectionRange(el.value.length, el.value.length); } catch (_) { /* поле без выделения */ } } };
    if (inp.dataset.pt != null) {
        const row = Number(inp.dataset.pt);
        if (pts.length >= 2) {
            const used = new Set();
            state.points = pts.slice(0, 8).map((p, i) => {
                let name = p.name && !used.has(p.name) ? p.name : 'ABCDEFGHKLMN'.split('').find((c) => !used.has(c)) || 'P' + i;
                used.add(name);
                return { name, x: p.c[0], y: p.c[1] };
            });
            buildFields();
            focusOn('geoPy' + (state.points.length - 1));
        } else {
            const p = pts[0];
            const pt = state.points[row];
            pt.x = p.c[0];
            pt.y = p.c[1];
            if (p.name && !state.points.some((q, i) => i !== row && q.name === p.name)) pt.name = p.name;
            buildFields();
            focusOn('geoPy' + row);
        }
    } else {
        const v = inp.dataset.vec;
        const dim3 = pts.some((p) => p.c.length === 3);
        const put = (key, c) => { state.vec[key] = { x: c[0], y: c[1], z: c[2] || '' }; };
        if (pts.length >= 2) { put('a', pts[0].c); put('b', pts[1].c); } else put(v, pts[0].c);
        state.vec.dim3 = dim3;
        buildFields();
        focusOn('geoV' + (pts.length >= 2 ? 'b' : v) + (dim3 ? 'z' : 'y'));
    }
    clearTimeout(typeTimer);
    refresh(true);
    ae('paste_points', { fig: state.fig, n: pts.length });
    return true;
}
function storeInput(inp) {
    if (inp.dataset.q) state.inputs[inp.dataset.q] = inp.value;
    else if (inp.dataset.pt != null) state.points[Number(inp.dataset.pt)][inp.dataset.c] = inp.value;
    else if (inp.dataset.vec) state.vec[inp.dataset.vec][inp.dataset.c] = inp.value;
}

/* ── примеры ──────────────────────────────────────────────────────────────── */
function buildSamples() {
    const box = $('geoSamples');
    box.innerHTML = '<span class="geo-samples-l">' + esc(t('samples')) + '</span>';
    const list = (CFG.samples || {})[state.fig] || [];
    list.forEach((s, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-chip';
        setText(b, readerText(s.t, NOTATION));
        b.dataset.geoSample = String(i);
        b.addEventListener('click', () => applySample(s));
        box.appendChild(b);
    });
    box.hidden = !list.length;
}
function applySample(s, auto) {
    if (state.fig === 'coords') {
        state.points = (s.pts || []).map((p) => ({ name: p[0], x: String(p[1]), y: String(p[2]) }));
    } else if (state.fig === 'vectors') {
        state.vec = { a: { x: String(s.a[0]), y: String(s.a[1]), z: s.a[2] != null ? String(s.a[2]) : '' }, b: { x: String(s.b[0]), y: String(s.b[1]), z: s.b[2] != null ? String(s.b[2]) : '' }, dim3: s.a.length > 2 };
    } else {
        if (s.v && s.v !== state.variant) state.variant = s.v;
        if (s.n) state.n = s.n;
        state.inputs = Object.assign({}, s.s || {});
        buildVariant();
    }
    state.targets = s.find ? [].concat(s.find) : null;
    state.method = null;
    state.sol = 0;
    buildFields();
    refresh(!auto);
    if (!auto) ae('sample', { fig: state.fig });
}

/* ── недавние задачи ──────────────────────────────────────────────────────────
   Ученик решает пять задач из домашней работы подряд: вернуться к третьей —
   одно нажатие, а не набор заново. До шести последних решённых условий в
   настройках тула (__toolSettings, этот браузер); запоминается условие, на
   котором ввод затих и ответ показан. */
const RECENT_MAX = 6;
let recent = [];
let recentTimer = 0;
function recentEntry() {
    if (state.fig === 'coords') return { f: 'coords', p: state.points.filter((p) => String(p.x).trim() && String(p.y).trim()).map((p) => [p.name, String(p.x).trim(), String(p.y).trim()]) };
    if (state.fig === 'vectors') return { f: 'vectors', a: ['x', 'y', 'z'].map((c) => String(state.vec.a[c] || '').trim()), b: ['x', 'y', 'z'].map((c) => String(state.vec.b[c] || '').trim()), d3: !!state.vec.dim3 };
    const i = {};
    for (const [k, v] of Object.entries(state.inputs)) if (String(v || '').trim()) i[k] = String(v).trim();
    const meta = figureMeta(state.fig);
    return { f: state.fig, v: state.variant || '', n: meta && meta.n ? state.n : 0, i };
}
function recentValid(r) {
    if (!r || typeof r !== 'object' || !figureMeta(r.f)) return false;
    if (r.f === 'coords') return Array.isArray(r.p) && r.p.length >= 2;
    if (r.f === 'vectors') return Array.isArray(r.a) && Array.isArray(r.b);
    return r.i && typeof r.i === 'object' && Object.keys(r.i).length > 0;
}
function recentLabel(r) {
    let body;
    if (r.f === 'coords') body = r.p.map(([n, x, y]) => n + '(' + x + coordSep() + y + ')').join(', ');
    else if (r.f === 'vectors') body = ['a', 'b'].map((k) => k + '(' + r[k].filter(Boolean).join(coordSep()) + ')').join(', ');
    else {
        let fig = null;
        try { fig = makeFigure(r.f, { variant: r.v || undefined, n: r.n || undefined }); } catch (_) { fig = null; }
        body = Object.entries(r.i).map(([id, v]) => (fig && fig.qty[id] ? subPlain(symbolText(fig, id, sym)) : id) + ' = ' + v).join(listSep());
    }
    return figTitle(r.f, r.v, r.n || 0, false) + labelSep() + body;
}
function recentRender() {
    const box = $('geoRecent');
    const list = $('geoRecentList');
    if (!box || !list) return;
    list.textContent = '';
    recent.forEach((r, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-chip';
        b.dataset.geoRecent = String(i);
        const label = recentLabel(r);
        setText(b, label.length > 64 ? label.slice(0, 63) + '…' : label);
        b.title = label;
        list.appendChild(b);
    });
    box.hidden = !recent.length;
}
function recentInit() {
    let saved = [];
    try { saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'recent', []) : []; } catch (_) { saved = []; }
    recent = Array.isArray(saved) ? saved.filter(recentValid).slice(0, RECENT_MAX) : [];
    recentRender();
    $('geoRecentList').addEventListener('click', (e) => {
        const b = e.target.closest('[data-geo-recent]');
        const r = b && recent[Number(b.dataset.geoRecent)];
        if (!r) return;
        if (r.f === 'coords') state.points = r.p.map(([name, x, y]) => ({ name, x, y }));
        else if (r.f === 'vectors') state.vec = { a: { x: r.a[0], y: r.a[1], z: r.a[2] }, b: { x: r.b[0], y: r.b[1], z: r.b[2] }, dim3: !!r.d3 };
        else state.inputs = Object.assign({}, r.i);
        selectFigure(r.f, { variant: r.v || undefined, n: r.n || undefined, keepInputs: true });
        ae('recent', { fig: r.f });
    });
    $('geoRecentClear').addEventListener('click', () => {
        recent = [];
        saveSetting('recent', recent);
        recentRender();
        ae('recent_clear', {});
    });
}
/** Запомнить условие, когда ответ показан и ввод затих. */
function recentLater() {
    clearTimeout(recentTimer);
    // Снимок — условие, на котором показан ответ; любой следующий пересчёт таймер снимает
    // (refresh), и в список не попадает то, что набрано после: «a = 5 6», «3, 4, 7».
    const r = recentEntry();
    recentTimer = setTimeout(() => {
        if (!recentValid(r)) return;
        const key = JSON.stringify(r);
        recent = [r].concat(recent.filter((x) => JSON.stringify(x) !== key)).slice(0, RECENT_MAX);
        saveSetting('recent', recent);
        recentRender();
    }, 1500);
}

/* ── слои чертежа ─────────────────────────────────────────────────────────── */
const TRI_OVERLAYS = [['heights', 'overlay_ha', ['ha', 'hb', 'hc']], ['medians', 'overlay_ma', ['ma', 'mb', 'mc']],
    ['bisectors', 'overlay_la', ['la', 'lb', 'lc']], ['incircle', 'overlay_r', ['r']], ['circum', 'overlay_R', ['R']]];
function buildOverlays() {
    const box = $('geoOverlays');
    box.innerHTML = '';
    if (isCoords()) { box.hidden = true; return; }
    const list = state.fig === 'triangle' ? TRI_OVERLAYS : [['all', 'overlay_all', null]];
    for (const [key, label] of list) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-chip' + (state.overlays[key] ? ' on' : '');
        b.setAttribute('aria-pressed', state.overlays[key] ? 'true' : 'false');
        b.dataset.geoOverlay = key;
        b.textContent = t(label);
        box.appendChild(b);
    }
    box.hidden = false;
}
function overlayMap(fig) {
    const o = {};
    if (state.fig === 'triangle') {
        for (const [key, , ids] of TRI_OVERLAYS) if (state.overlays[key]) ids.forEach((id) => { o[id] = true; });
    } else if (state.overlays.all && fig) {
        Object.keys(fig.qty).forEach((id) => { o[id] = true; });
    }
    return o;
}

/* ── справочник формул ────────────────────────────────────────────────────── */
function buildFormulas() {
    const box = $('geoFormulas');
    box.innerHTML = '';
    if (isCoords()) {
        const list = state.fig === 'coords'
            ? [['distance', '|AB| = \\sqrt{(x_B - x_A)^2 + (y_B - y_A)^2}'], ['midpoint', 'M\\left(\\frac{x_A + x_B}{2}' + coordTex() + '\\frac{y_A + y_B}{2}\\right)'],
                ['line_two_points', '\\frac{x - x_A}{x_B - x_A} = \\frac{y - y_A}{y_B - y_A}'], ['circle_equation', '(x - x_0)^2 + (y - y_0)^2 = R^2'],
                ['shoelace', symbols('@area', { qty: {} }, sym) + ' = \\frac{1}{2}\\left|\\sum (x_i y_{i+1} - x_{i+1} y_i)\\right|']]
            : [['vector_length', '|\\vec a| = \\sqrt{x^2 + y^2}'], ['dot_product', '\\vec a\\cdot\\vec b = x_1x_2 + y_1y_2'],
                ['vector_angle', '\\cos\\varphi = \\frac{\\vec a\\cdot\\vec b}{|\\vec a|\\,|\\vec b|}']];
        for (const [n, f] of list) addFormula(box, ruleName(n), f);
        return;
    }
    const fig = makeFigure(state.fig, { variant: state.variant, n: state.n });
    const seen = new Set();
    for (const r of fig.rules) {
        const g = symbols(r.general, fig, sym);
        const key = r.name + '|' + g;
        if (seen.has(key)) continue;
        seen.add(key);
        if (seen.size > 60) break;
        addFormula(box, ruleName(r.name), g);
    }
}
function addFormula(box, name, f) {
    const d = document.createElement('div');
    d.className = 'geo-formula';
    const b = document.createElement('b');
    b.textContent = name;
    const m = document.createElement('div');
    d.appendChild(b);
    d.appendChild(m);
    box.appendChild(d);
    tex(m, f);
}

/* ── чтение «дано» ────────────────────────────────────────────────────────── */
function setFieldError(inp, msg) {
    const err = $(inp.id + 'Err') || (inp.dataset.pt != null ? $('geoPt' + inp.dataset.pt + 'Err') : inp.dataset.vec ? $('geoV' + inp.dataset.vec + 'Err') : null);
    if (msg) {
        inp.setAttribute('aria-invalid', 'true');
        if (err) { setText(err, msg); err.hidden = false; }
    } else {
        inp.removeAttribute('aria-invalid');
        if (err && !(inp.closest('.geo-pt') && inp.closest('.geo-pt').querySelector('[aria-invalid=true]'))) { err.textContent = ''; err.hidden = true; }
    }
}

/** Поля → givens. Ошибки показываются у полей, словами. */
function readGivens(fig) {
    const givens = {};
    const conversions = {};
    let bad = 0;
    const errs = [];
    const fmt = formatter();
    const no = (inp, code, key, raw) => { bad++; errs.push({ q: inp.dataset.q, code, raw }); setFieldError(inp, t(key)); };
    document.querySelectorAll('#geoFields .geo-in[data-q]').forEach((inp) => {
        const id = inp.dataset.q;
        if (inp.disabled) return;
        const raw = inp.value.trim();
        inp.classList.toggle('given', !!raw);
        if (!raw) { setFieldError(inp, null); return; }
        const q = fig.qty[id];
        let r;
        if (q.kind === 'angle') r = parseAngle(raw, state.angle === 'rad' ? 'rad' : 'deg');
        else if (q.kind === 'ratio') r = evalExprSafe(raw);
        else r = parseMeasure(raw, q.kind, state.unit);
        if (r.error) {
            if (r.error === 'unit_kind') no(inp, 'unit_kind', 'field_err_unit', raw);
            else if (r.error === 'minutes') no(inp, 'minutes', 'field_err_minutes', raw);
            else no(inp, 'unreadable', 'field_err', raw);
            return;
        }
        if (!(r.v.n > 0)) { no(inp, 'not_positive', 'field_err_positive', raw); return; }
        // Число за пределами двойной точности (1e200) решатель видел «противоречием» —
        // «такой фигуры не существует» про число, которое просто слишком велико.
        if (r.v.n > BIG) { no(inp, 'too_big', 'field_err_big', raw); return; }
        if (q.kind === 'angle' && !(r.v.n < (id === 'phi' && state.fig === 'sector' ? 360 : 180))) { no(inp, 'angle_big', 'field_err_angle', raw); return; }
        setFieldError(inp, null);
        givens[id] = { v: r.v, typedDecimals: r.typedDecimals || 0 };
        if (r.from) {
            const raw0 = fmt.value(r.raw).tex;
            const u = (CFG.units || {})[r.rawUnit] || r.rawUnit;
            const pw = q.kind === 'area' ? '^2' : q.kind === 'volume' ? '^3' : '';
            conversions[id] = { tex: raw0 + '\\,\\text{' + u + '}' + (r.rawUnit === 'l' || r.rawUnit === 'ml' ? '' : pw) };
        }
    });
    return { givens, conversions, bad, errs };
}
/** Потолок величины: дальше квадрат и куб уходят за двойную точность. */
const BIG = 1e15;
function evalExprSafe(raw) {
    const r = evalExpr(String(raw).replace(',', '.'));
    return r;
}

/* ── главный пересчёт ─────────────────────────────────────────────────────── */
function refresh(fromUser) {
    clearTimeout(recentTimer);
    if (state.fig === 'coords') return refreshCoords(fromUser);
    if (state.fig === 'vectors') return refreshVectors(fromUser);
    const fig = makeFigure(state.fig, { variant: state.variant, n: state.n });
    state.figObj = fig;
    const { givens, conversions, bad, errs } = readGivens(fig);
    if (fromUser && errs.length) refused('field_' + errs[0].code, { mode: 'shapes', fig: state.fig, q: errs[0].q }, errs[0].raw);
    const presetIds = Object.keys(fig.presets || {});
    const typed = Object.keys(givens);
    for (const id of presetIds) givens[id] = { v: fig.presets[id], typedDecimals: 0 };
    state.givens = givens;
    state.conversions = conversions;
    state.presetIds = presetIds;
    if (!typed.length) {
        state.res = null;
        showStatus([{ cls: '', text: bad ? '' : t('status_empty') }].filter((x) => x.text));
        renderEmpty();
        saveHash();
        return;
    }
    let res;
    try { res = solve(fig, givens, { pi: piValue() }); } catch (e) {
        // Сбой решателя — не «такой фигуры нет»: это наша ошибка, и её видно в телеметрии.
        report(e, 'solve');
        res = { status: 'contradiction', reason: { key: 'no_solution' }, solutions: [], failed: true };
    }
    state.res = res;
    if (state.sol >= res.solutions.length) state.sol = 0;
    try { renderAll(fromUser); } catch (e) {
        report(e, 'render', t('calc_failed'));
        renderEmpty(t('calc_failed'));
        return;
    }
    if (fromUser && !errs.length) {
        const what = { mode: 'shapes', fig: state.fig, variant: state.variant || '', n: state.fig === 'polygon' || figureMeta(state.fig).n ? state.n : undefined, given: typed.slice().sort().join(',') };
        if (res.status === 'ok') {
            stage('solved', { fig: state.fig });
            resultShown(Object.assign(what, { sols: res.solutions.length, numeric: res.solutions.some((s) => s.numeric) ? 1 : 0 }),
                JSON.stringify([state.fig, state.variant, state.n, state.inputs, state.unit]));
            recentLater();
        } else if (res.status === 'contradiction') {
            refused(res.failed ? 'solve_failed' : 'contradiction', Object.assign(what, { why: (res.reason && res.reason.key) || '' }), givenText());
        } else if (res.status === 'partial' || res.status === 'insufficient') {
            refused('not_enough', Object.assign(what, { need: res.need }), givenText());
        }
    }
}

function renderEmpty(msg) {
    $('geoAnswerPanel').hidden = true;
    $('geoSolutionPanel').hidden = true;
    $('geoSols').hidden = true;
    const c = $('geoCanvas');
    c.innerHTML = '<div class="geo-empty" id="geoEmpty">' + esc(msg || t('drawing_empty')) + '</div>';
    $('geoCaption').textContent = '';
    state.lastLayout = null;
    drawingTools(false);
}
/**
 * Слои и выгрузка чертежа — только когда чертёж есть: кнопка PNG над пустым
 * полем молча ничего не делала бы.
 */
function drawingTools(on) {
    const ov = $('geoOverlays');
    ov.hidden = !on || isCoords() || !ov.children.length;
    const ex = document.querySelector('#geoDrawPanel .geo-exp');
    if (ex) ex.hidden = !on;
    // «Построить циркулем и линейкой» — у треугольника с найденными сторонами.
    const tc = $('geoToConstruct');
    if (tc) tc.hidden = !(on && state.fig === 'triangle' && triangleSidesCm());
}
/** Стороны решённого треугольника в сантиметрах (для построения) или null. */
function triangleSidesCm() {
    const res = state.res;
    const s = res && res.solutions && res.solutions[state.sol];
    if (state.fig !== 'triangle' || !s || !s.values) return null;
    const k = (LENGTH[state.unit][0] / LENGTH[state.unit][1]) * 100;
    const v = (id) => (s.values[id] && Number.isFinite(s.values[id].n) ? s.values[id].n * k : NaN);
    const out = { a: v('a'), b: v('b'), c: v('c') };
    return out.a > 0 && out.b > 0 && out.c > 0 ? out : null;
}

function renderAll() {
    const res = state.res;
    const fig = state.figObj;
    renderStatus(res, fig);
    if (!res.solutions.length) {
        renderEmpty();
        saveHash();
        return;
    }
    renderSolutionsSwitch(res);
    const s = res.solutions[state.sol];
    renderTable(fig, s, res);
    renderDrawing();
    renderSolution(fig, s, res);
    saveHash();
}

/* ── статус: не хватает / противоречие / два решения ──────────────────────── */
function showStatus(items, suggest) {
    const box = $('geoStatus');
    box.innerHTML = '';
    for (const it of items) {
        const p = document.createElement('p');
        p.className = 'tc-msg' + (it.cls ? ' ' + it.cls : '');
        setText(p, it.text);
        box.appendChild(p);
    }
    if (suggest && suggest.length) {
        const row = document.createElement('div');
        row.className = 'geo-suggest';
        for (const id of suggest) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'tc-chip';
            b.innerHTML = fill(esc(t('sym_name')), { sym: symHtml(state.figObj, id), name: esc(qName(state.fig, id)) });
            b.addEventListener('click', () => {
                const inp = $('geoIn_' + id);
                if (!inp) return;
                const more = inp.closest('details');
                if (more) more.open = true;
                inp.focus();
            });
            row.appendChild(b);
        }
        box.appendChild(row);
    }
    box.hidden = !box.children.length;
}

function reasonText(reason, fig) {
    const why = CFG.why || {};
    const fmt = formatter();
    const sy = (id) => (id && fig.qty[id] ? symbolText(fig, id, sym) : id || '');
    const val = (v, id) => {
        if (!v) return '';
        const kind = id && fig.qty[id] ? fig.qty[id].kind : null;
        return fmt.quantity(v, kind).text;
    };
    const map = {
        rule: reason.rule ? ruleName(reason.rule) : '',
        out: sy(reason.out),
        got: val(reason.got, reason.out),
        given: val(reason.given, reason.out),
        big: sy(reason.big),
        others: (reason.others || []).map(sy).join(' + '),
        ins: listJoin((reason.ins || []).map(sy), LOCALE),
        bigv: val(reason.bigV, reason.big),
        sum: val(reason.sumV, reason.big) || val(reason.sum, 'alpha'),
    };
    // Отказ формулы без подробностей (какая сторона больше) — у ключа есть
    // короткий вариант «_plain»: иначе в тексте остались бы пустые места.
    const key = !reason.big && why[reason.key + '_plain'] ? reason.key + '_plain' : reason.key;
    return fill(why[key] || why.impossible || reason.key, map);
}

function renderStatus(res, fig) {
    const items = [];
    let suggest = null;
    if (res.status === 'partial' || res.status === 'insufficient') {
        items.push({ cls: 'warn', text: fill(t('status_partial'), { n: res.need }) });
        suggest = res.suggest;
    } else if (res.status === 'contradiction') {
        items.push({ cls: 'bad', text: fill(t('status_contradiction'), { why: reasonText(res.reason || { key: 'no_solution' }, fig) }) });
    } else if (res.status === 'ok') {
        if (res.solutions.length === 2) items.push({ cls: 'good', text: t('status_two') });
        if (res.solutions.length >= 3) items.push({ cls: 'good', text: t('status_three') });
        const s = res.solutions[state.sol] || res.solutions[0];
        if (s.numeric) items.push({ cls: '', text: t('status_numeric') });
        if (s.mirror) items.push({ cls: '', text: t('status_mirror') });
        for (const w of (s.warnings || []).filter((x) => x.key === 'rounding').slice(0, 2)) {
            items.push({ cls: 'warn', text: fill(t('status_rounding'), { why: reasonText(Object.assign({}, w, { key: 'mismatch' }), fig) }) });
        }
    }
    showStatus(items, suggest);
}

function renderSolutionsSwitch(res) {
    const box = $('geoSols');
    box.innerHTML = '';
    if (res.solutions.length < 2) { box.hidden = true; return; }
    res.solutions.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-seg-b' + (i === state.sol ? ' on' : '');
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', i === state.sol ? 'true' : 'false');
        b.dataset.geoSol = String(i);
        b.textContent = fill(t('solution_n'), { n: i + 1 });
        box.appendChild(b);
    });
    box.hidden = false;
}

/* ── таблица величин и «призрачные» значения в полях ─────────────────────── */
const DEFAULT_TARGETS = {
    triangle: ['S', 'c', 'a', 'b', 'gamma', 'alpha', 'beta', 'P'], square: ['S', 'd', 'a'], rectangle: ['d', 'S', 'b', 'a'],
    parallelogram: ['S', 'd1', 'b'], rhombus: ['S', 'a', 'd2'], trapezoid: ['S', 'h', 'c'], quad: ['S', 'phi'],
    circle: ['S', 'C', 'r'], sector: ['l', 'Ss', 'r', 'phi'], polygon: ['S', 'a'], ellipse: ['S', 'P'],
    cube: ['V', 'S', 'a'], cuboid: ['V', 'd', 'S'], prism: ['V', 'S'], pyramid: ['V', 'S', 'ap'], frustum_pyramid: ['V', 'S'],
    cylinder: ['V', 'S', 'h'], cone: ['V', 'S', 'l'], frustum_cone: ['V', 'S'], sphere: ['V', 'S', 'R'], cap: ['V', 'Scap'],
    sphere_sector: ['V', 'S'], layer: ['V', 'Szone'],
};
function currentTargets(fig, s) {
    const known = s ? s.values : {};
    const given = state.givens || {};
    let tg = (state.targets || []).filter((id) => known[id] && !given[id]);
    if (!tg.length) {
        const pref = DEFAULT_TARGETS[state.fig] || fig.order;
        const first = pref.find((id) => known[id] && !given[id]);
        tg = first ? [first] : [];
    }
    return tg;
}

function renderTable(fig, s, res) {
    const panel = $('geoAnswerPanel');
    const body = $('geoTableBody');
    const fmt = formatter();
    body.innerHTML = '';
    const targets = currentTargets(fig, s);
    const given = state.givens || {};
    // Второстепенные величины (высоты, медианы, радиусы…) — за «Ещё»: иначе
    // двадцать строк таблицы отодвигают решение за второй экран. Искомое и
    // данное видны всегда.
    const secondary = fig.order.filter((id) => !isPrimaryQty(fig, id));
    const foldable = secondary.length >= 3;
    let folded = 0;
    for (const id of fig.order) {
        const v = s.values[id];
        const q = fig.qty[id];
        const tr = document.createElement('tr');
        if (foldable && !state.allValues && !isPrimaryQty(fig, id) && !targets.includes(id) && !given[id]) { tr.hidden = true; folded++; }
        const td = document.createElement('td');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'geo-row' + (given[id] ? ' giv' : '');
        b.dataset.geoTarget = id;
        const isT = targets.includes(id);
        b.setAttribute('aria-pressed', isT ? 'true' : 'false');
        if (given[id] || !v) b.disabled = true;
        let vHtml = '—';
        if (v) {
            const f = fmt.quantity(v, q.kind);
            if (q.kind === 'angle') {
                const a = f.main;
                vHtml = esc((a.approx ? '≈ ' : '') + a.text) + (a.exact && state.angle === 'rad' ? '<small>≈ ' + esc(a.dec) + '</small>' : '')
                    + (state.angle !== 'deg' && !a.exact ? '' : '');
            } else if (f.main.exact) {
                vHtml = esc(f.text) + '<small>≈ ' + esc(f.dec) + '</small>';
            } else {
                vHtml = esc((f.approx ? '≈ ' : '') + f.text);
            }
        }
        b.innerHTML = '<span class="pin" aria-hidden="true">' + (isT ? '●' : given[id] ? '✓' : '○') + '</span>'
            + '<span class="n"><b>' + symHtml(fig, id) + '</b>' + esc(qName(state.fig, id)) + '</span><span class="v">' + vHtml + '</span>';
        b.setAttribute('aria-label', fill(t(given[id] ? 'values_row_aria_given' : 'values_row_aria'), { sym: subPlain(symbolText(fig, id, sym)), name: qName(state.fig, id), value: b.querySelector('.v').textContent }));
        td.appendChild(b);
        tr.appendChild(td);
        body.appendChild(tr);
        // «Призрак» в поле: найденное значение как подсказка.
        const inp = $('geoIn_' + id);
        if (inp && !inp.disabled) inp.placeholder = v && !given[id] ? (fmt.quantity(v, q.kind).approx ? '≈ ' : '') + fmt.quantity(v, q.kind).text : '';
    }
    if (foldable && (folded || state.allValues)) {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'geo-vals-more';
        b.dataset.geoAllvals = '1';
        b.setAttribute('aria-expanded', state.allValues ? 'true' : 'false');
        b.textContent = state.allValues ? t('values_less') : fill(t('values_more'), { n: folded });
        td.appendChild(b);
        tr.appendChild(td);
        body.appendChild(tr);
    }
    $('geoTable').hidden = false;
    $('geoObjs').hidden = true;
    // Вид треугольника.
    const flags = $('geoFlags');
    flags.innerHTML = '';
    if (state.fig === 'triangle' && res.status === 'ok') {
        const k = classify(s.values);
        for (const key of ['kind_' + k.byAngle, 'kind_' + k.bySide]) {
            const sp = document.createElement('span');
            sp.className = 'geo-flag';
            sp.textContent = t(key);
            flags.appendChild(sp);
        }
    }
    flags.hidden = !flags.children.length;
    panel.hidden = false;
}

/* ── чертёж ───────────────────────────────────────────────────────────────── */
function canvasSize() {
    const c = $('geoCanvas');
    const w = Math.max(280, Math.round(c.clientWidth || 600));
    const h = Math.round(Math.max(250, Math.min(480, w * 0.64)));
    return { w, h };
}
/**
 * Высота поля — по пропорции фигуры: плоский вытянутый треугольник не висит
 * посреди пустого прямоугольника, а высокий конус получает всю высоту. Пока
 * вершину тянут, высота прежняя — фигура не прыгает под пальцем.
 */
function fitCanvasHeight(w, h, b, keep) {
    if (keep && state.lastLayout) return state.lastLayout.layout.height;
    return fitHeight(w, h, b);
}

function drawOptions(fig, s, o = {}) {
    const fmt = formatter();
    const given = new Set(Object.keys(state.givens || {}));
    const targets = new Set(currentTargets(fig, s));
    return Object.assign({
        label: makeLabeler(fig, s.values, fmt, sym, state.labels),
        highlight: targets, given, overlays: overlayMap(fig), n: state.n, fmt,
        draggable: state.fig === 'triangle' && state.variant === 'general' && !o.export,
    }, o);
}

function renderDrawing(opts = {}) {
    const c = $('geoCanvas');
    if (state.fig === 'coords' || state.fig === 'vectors') return renderCoordDrawing();
    const res = state.res;
    if (!res || !res.solutions.length) return;
    const fig = state.figObj;
    const s = res.solutions[state.sol] || res.solutions[0];
    if (!s) return;
    const nums = numbers(s.values);
    const complete = Object.keys(fig.qty).every((id) => nums[id] != null);
    if (!complete) {
        c.innerHTML = '<div class="geo-empty">' + esc(t('drawing_partial')) + '</div>';
        $('geoCaption').textContent = '';
        state.lastLayout = null;
        drawingTools(false);
        return;
    }
    const { w, h } = canvasSize();
    const d = drawFigure(state.fig, nums, drawOptions(fig, s, { points: opts.points }));
    if (!d) return;
    const hh = fitCanvasHeight(w, h, opts.bounds || d.sheet.bounds(), !!opts.scale);
    const r = d.sheet.render({ width: w, height: hh, bounds: opts.bounds, scale: opts.scale, title: t('drawing_label') });
    c.innerHTML = r.svg;
    drawingTools(true);
    state.lastLayout = { layout: r.layout, points: d.points, bounds: opts.bounds || d.sheet.bounds(), scale: r.layout.scale, sheet: d.sheet };
    const unitName = (CFG.units || {})[state.unit] || state.unit;
    const solid = figureMeta(state.fig).group === 'solid';
    let cap = solid ? t('drawing_caption_solid') : state.fig === 'quad' ? t('drawing_caption_quad') : fill(t('drawing_caption'), { unit: unitName });
    if (!solid && state.fig === 'triangle' && state.variant === 'general') cap += ' ' + t('drawing_drag');
    setText($('geoCaption'), cap);
}

function renderCoordDrawing() {
    const c = $('geoCanvas');
    const r0 = state.coordRes;
    if (!r0 || !r0.ok) return;
    const { w, h } = canvasSize();
    const fmt = formatter();
    let d;
    if (state.fig === 'coords') d = drawPoints(r0.pts, r0.result, { fmt, sep: coordSep(), draggable: true });
    else d = drawVectors(r0.a, r0.b, r0.result, { fmt, label: (id) => symbolText({ qty: r0.result.qty }, id, sym) });
    const r = d.sheet.render({ width: w, height: h, bounds: state.drag && state.lastLayout ? state.lastLayout.bounds : d.bounds, scale: state.drag && state.lastLayout ? state.lastLayout.scale : undefined });
    c.innerHTML = r.svg;
    drawingTools(true);
    state.lastLayout = { layout: r.layout, bounds: state.drag && state.lastLayout ? state.lastLayout.bounds : d.bounds || d.sheet.bounds(), scale: r.layout.scale, sheet: d.sheet };
    $('geoCaption').textContent = state.fig === 'coords' ? t('drawing_drag') : '';
}

/* ── перетаскивание вершин ────────────────────────────────────────────────── */
function bindDrag() {
    const c = $('geoCanvas');
    c.addEventListener('pointerdown', (e) => {
        const h = e.target.closest && e.target.closest('[data-geo-handle]');
        if (!h || !state.lastLayout) return;
        e.preventDefault();
        state.drag = { id: h.getAttribute('data-geo-handle'), pid: e.pointerId };
        try { c.setPointerCapture(e.pointerId); } catch (_) { /* старый браузер */ }
        c.classList.add('dragging');
        ae('drag_start', { fig: state.fig });
    });
    /* Moves are drawn once per frame: the frame draws the LATEST move of the
       drag it was asked for. Lifting the finger before that frame used to
       null state.drag under it — the frame threw on `state.drag.id` (Sentry
       TIMBRICA-FRONTEND-QG, Yandex/Android) and the last stretch of the drag
       was lost: the vertex stayed where the previous frame had put it. Now
       the release draws the pending move itself, while the drag still owns
       the scale, and a frame never outlives the drag it belongs to. */
    let raf = 0;
    let pending = null;               // { drag, wpt } — not drawn yet
    const drawPending = () => {
        raf = 0;
        const p = pending;
        pending = null;
        if (p && state.drag === p.drag) dragTo(p.drag.id, p.wpt);
    };
    c.addEventListener('pointermove', (e) => {
        const drag = state.drag;
        if (!drag || e.pointerId !== drag.pid) return;
        const rect = c.getBoundingClientRect();
        const L = state.lastLayout.layout;
        const sx = (e.clientX - rect.left) * (L.width / rect.width);
        const sy = (e.clientY - rect.top) * (L.height / rect.height);
        pending = { drag, wpt: L.toWorld(sx, sy) };
        if (!raf) raf = requestAnimationFrame(drawPending);
    });
    const end = (e) => {
        if (!state.drag || (e && e.pointerId !== state.drag.pid)) return;
        if (raf) { cancelAnimationFrame(raf); drawPending(); }
        state.drag = null;
        c.classList.remove('dragging');
        refresh(true);
    };
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', end);
}

/** Число для поля: до двух знаков, с запятой локали. */
function fieldNum(x, d = 2) {
    const k = Math.pow(10, d);
    return String(Math.round(x * k) / k).replace('.', formatter().dec);
}

function dragTo(id, wpt) {
    if (state.fig === 'coords') {
        const i = state.points.findIndex((p) => p.name === id);
        if (i < 0) return;
        const snap = (v) => (Math.abs(v - Math.round(v)) < 0.15 ? Math.round(v) : Math.round(v * 10) / 10);
        state.points[i].x = fieldNum(snap(wpt[0]), 1);
        state.points[i].y = fieldNum(snap(wpt[1]), 1);
        const xi = $('geoPx' + i); const yi = $('geoPy' + i);
        if (xi) xi.value = state.points[i].x;
        if (yi) yi.value = state.points[i].y;
        refreshCoords(false);
        return;
    }
    if (state.fig !== 'triangle' || !state.lastLayout || !state.lastLayout.points) return;
    const P = Object.assign({}, state.lastLayout.points);
    P[id] = wpt;
    const d = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]);
    const v = valuesFromSides(d(P.B, P.C), d(P.A, P.C), d(P.A, P.B));
    if (!v || Math.min(v.alpha, v.beta, v.gamma) < 1) return;
    // Данные человека остаются данными — меняются только их значения.
    const given = Object.keys(state.inputs).filter((k) => String(state.inputs[k] || '').trim());
    for (const k of given) {
        if (v[k] == null) continue;
        const inp = $('geoIn_' + k);
        const fig = state.figObj;
        const val = fig.qty[k].kind === 'angle' ? fieldNum(v[k], 1) : fieldNum(v[k], 2);
        state.inputs[k] = val;
        if (inp) inp.value = val;
    }
    const fig = makeFigure('triangle', { variant: 'general' });
    const { givens } = readGivens(fig);
    state.givens = givens;
    try { state.res = solve(fig, givens, { pi: piValue() }); } catch (_) { return; }
    if (!state.res.solutions.length) return;
    state.sol = 0;
    const s = state.res.solutions[0];
    renderTable(fig, s, state.res);
    renderStatus(state.res, fig);
    // Чертёж — из точек мыши, масштаб прежний: фигура не прыгает под пальцем.
    renderDrawing({ points: P, bounds: state.lastLayout.bounds, scale: state.lastLayout.scale });
    state.lastLayout.points = P;
}

/* ── ход решения ──────────────────────────────────────────────────────────── */
function renderSolution(fig, s, res) {
    const panel = $('geoSolutionPanel');
    const fmt = formatter();
    const targets = currentTargets(fig, s);
    if (!targets.length || !s.state) { panel.hidden = true; state.solution = null; return; }
    let steps;
    const mbox = $('geoMethods');
    mbox.innerHTML = '';
    let methods = [];
    if (targets.length === 1) {
        methods = methodsFor(fig, s.state, targets[0], { pi: piValue(), fig });
    }
    if (methods.length > 1) {
        if (!state.method || !methods.some((m) => m.name === state.method)) state.method = methods[0].name;
        for (const m of methods) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'tc-seg-b' + (m.name === state.method ? ' on' : '');
            b.setAttribute('role', 'tab');
            b.setAttribute('aria-selected', m.name === state.method ? 'true' : 'false');
            b.dataset.geoMethod = m.name;
            b.textContent = ruleName(m.name);
            mbox.appendChild(b);
        }
        mbox.hidden = false;
        steps = methods.find((m) => m.name === state.method).steps;
    } else {
        mbox.hidden = true;
        steps = chainFor(fig, s.state, targets);
    }
    const sol = composeSolution({ fig, state: s.state, givens: state.givens, targets, steps, fmt, sym, conversions: state.conversions, presetIds: state.presetIds });
    // Численный шаг — первым, названным.
    if (s.numeric && steps.some((st) => s.state.known[fig.rules[st.ri].ins[0]] || true)) {
        const found = s.numeric.params.filter((id) => s.state.known[id] && s.state.known[id].src === 'numeric');
        if (found.length) {
            const lines = [found.map((id) => symbols('@' + id, fig, sym) + ' \\approx ' + fmt.quantity(s.values[id], fig.qty[id].kind).tex).join(NOTATION.listTex)];
            sol.steps.unshift({ name: 'numeric', general: '', lines, text: { general: '', lines: lines.map(texToText) }, numeric: true });
        }
    }
    state.solution = sol;
    // Дано
    const g = $('geoGiven');
    g.innerHTML = '';
    sol.given.forEach((x, i) => {
        const sp = document.createElement('span');
        tex(sp, x.tex);
        g.appendChild(sp);
        if (x.preset) g.appendChild(document.createTextNode(' ' + t('preset_given')));
        if (i < sol.given.length - 1) g.appendChild(document.createTextNode(listSep()));
    });
    const f = $('geoFindList');
    f.innerHTML = '';
    sol.find.forEach((x, i) => {
        const sp = document.createElement('span');
        tex(sp, x.tex);
        f.appendChild(sp);
        if (i < sol.find.length - 1) f.appendChild(document.createTextNode(listSep()));
    });
    // Шаги
    const ol = $('geoSteps');
    ol.innerHTML = '';
    for (const st of sol.steps) {
        const li = document.createElement('li');
        const nm = document.createElement('div');
        nm.className = 'geo-step-name';
        nm.textContent = st.numeric ? t('numeric_step') : ruleName(st.name);
        li.appendChild(nm);
        if (st.general) {
            const gd = document.createElement('div');
            gd.className = 'geo-step-gen';
            tex(gd, st.general);
            li.appendChild(gd);
        }
        for (const ln of st.lines) {
            const d = document.createElement('div');
            d.className = 'geo-step-line';
            tex(d, ln);
            li.appendChild(d);
        }
        const notes = [];
        if (st.numeric) notes.push(t('numeric_line'));
        if (st.branchKey) notes.push((CFG.branch || {})[st.branchKey] || st.branchKey);
        if (st.note && st.note.key) notes.push(fill((CFG.branch || {})[st.note.key] || st.note.key, { alt: fmt.angle(st.note.alt).text }));
        if (notes.length) {
            const n = document.createElement('div');
            n.className = 'geo-step-note';
            setText(n, notes.join(' '));
            li.appendChild(n);
        }
        ol.appendChild(li);
    }
    const a = $('geoAnswer');
    a.innerHTML = '';
    sol.answer.forEach((x, i) => {
        const sp = document.createElement('span');
        tex(sp, x.tex);
        a.appendChild(sp);
        if (i < sol.answer.length - 1) a.appendChild(document.createTextNode(listSep()));
    });
    panel.hidden = false;
}

/* ── метод координат ──────────────────────────────────────────────────────── */
function parseCoord(raw) {
    const s = String(raw || '').trim();
    if (!s) return { empty: true };
    const r = evalExpr(s.replace(/^\+/, ''));
    if (r.error) return { error: true };
    return Math.abs(r.v.n) > BIG ? { error: true, big: true } : { v: r.v };
}
function refreshCoords(fromUser) {
    const fmt = formatter();
    const pts = [];
    let bad = 0;
    state.points.forEach((p, i) => {
        const xi = $('geoPx' + i); const yi = $('geoPy' + i);
        const x = parseCoord(p.x); const y = parseCoord(p.y);
        if (xi) setFieldError(xi, x.error ? t(x.big ? 'field_err_big' : 'field_err') : null);
        if (yi) setFieldError(yi, y.error ? t(y.big ? 'field_err_big' : 'field_err') : null);
        if (x.error || y.error) {
            if (!bad && fromUser) refused(x.big || y.big ? 'coord_too_big' : 'coord_unreadable', { mode: 'coords' }, x.error ? p.x : p.y);
            bad++;
        }
        if (x.v && y.v) pts.push({ name: p.name, x: x.v, y: y.v });
    });
    state.coordRes = null;
    if (pts.length < 2) {
        showStatus(bad ? [] : [{ cls: '', text: t('status_need_points') }]);
        renderEmpty();
        saveHash();
        return;
    }
    const result = solvePoints(pts, { fmt, sepTex: coordTex() });
    if (result.status !== 'ok') {
        showStatus([{ cls: 'bad', text: fill(t('status_contradiction'), { why: fill((CFG.why || {})[result.reason.key] || result.reason.key, { a: result.reason.a || '', b: result.reason.b || '' }) }) }]);
        renderEmpty();
        saveHash();
        return;
    }
    showStatus([]);
    state.coordRes = { ok: true, pts, result };
    // Три точки — это ещё и треугольник со всеми его величинами.
    let tri = null;
    if (pts.length === 3) {
        const [A, B, C] = pts;
        const d2 = (P, Q) => V.sqrt(V.add(V.sq(V.sub(Q.x, P.x)), V.sq(V.sub(Q.y, P.y))));
        const T = makeFigure('triangle', { variant: 'general' });
        const givens = { a: { v: d2(B, C) }, b: { v: d2(A, C) }, c: { v: d2(A, B) } };
        try {
            const tr = solve(T, givens, { pi: piValue() });
            if (tr.status === 'ok') tri = { fig: T, res: tr, givens };
        } catch (_) { /* треугольник — дополнение, не условие */ }
    }
    state.coordTri = tri;
    renderCoordsResult(result, tri);
    renderCoordDrawing();
    renderCoordSolution(result, pts, tri);
    saveHash();
    if (fromUser && !bad) {
        stage('solved', { fig: 'coords' });
        resultShown({ mode: 'coords', fig: 'coords', n: pts.length }, JSON.stringify(['coords', state.points]));
        recentLater();
    }
}

function renderCoordsResult(result, tri) {
    const fmt = formatter();
    const body = $('geoTableBody');
    body.innerHTML = '';
    const rows = [];
    const cq = (CFG.qty || {}).coords || {};
    for (const [id, v] of Object.entries(result.values)) {
        const q = result.qty[id];
        const name = /^[A-Z]{2}$/.test(id) ? cq.dist || id : cq[id] || id;
        rows.push([symbolText({ qty: result.qty }, id, sym), name, q.kind === 'angle' ? fmt.angle(v) : fmt.quantity(v, q.kind), q.kind]);
    }
    if (tri) {
        const s = tri.res.solutions[0];
        for (const id of ['alpha', 'beta', 'gamma', 'ha', 'hb', 'hc', 'ma', 'mb', 'mc', 'r']) {
            const q = tri.fig.qty[id];
            rows.push([symbolText(tri.fig, id, sym), qName('triangle', id, null), q.kind === 'angle' ? fmt.angle(s.values[id]) : fmt.quantity(s.values[id], q.kind), q.kind]);
        }
    }
    for (const [s0, name, f] of rows) {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        const exact = f.main && f.main.exact;
        td.innerHTML = '<div class="geo-row" style="cursor:default"><span class="n"><b>' + richHtml(s0) + '</b>' + esc(name) + '</span><span class="v">'
            + esc((f.approx && !exact ? '≈ ' : '') + f.text) + (exact ? '<small>≈ ' + esc(f.dec) + '</small>' : '') + '</span></div>';
        tr.appendChild(td);
        body.appendChild(tr);
    }
    $('geoTable').hidden = !rows.length;
    // Точки, прямые, окружности.
    const objs = $('geoObjs');
    objs.innerHTML = '';
    for (const o of result.objects) {
        const d = document.createElement('div');
        d.className = 'geo-obj';
        const n = document.createElement('span');
        n.className = 'n';
        const v = document.createElement('span');
        if (o.kind === 'point') {
            n.textContent = t(o.name === 'M' ? 'obj_midpoint' : o.name === 'O' ? 'obj_center' : 'obj_point');
            tex(v, o.name + '\\left(' + fmt.value(o.x).tex + coordTex() + fmt.value(o.y).tex + '\\right)');
        } else if (o.kind === 'line') {
            n.textContent = fill(t('obj_line'), { name: o.name });
            tex(v, o.tex.general + (o.tex.slope && o.tex.slope !== o.tex.general ? NOTATION.listTex + o.tex.slope : ''));
        } else if (o.kind === 'circle') {
            n.textContent = t('obj_circle');
            tex(v, o.tex);
        } else continue;
        d.appendChild(n);
        d.appendChild(v);
        objs.appendChild(d);
    }
    objs.hidden = !objs.children.length;
    const flags = $('geoFlags');
    flags.innerHTML = '';
    if (tri) {
        const k = classify(tri.res.solutions[0].values);
        for (const key of ['kind_' + k.byAngle, 'kind_' + k.bySide]) {
            const sp = document.createElement('span');
            sp.className = 'geo-flag';
            sp.textContent = t(key);
            flags.appendChild(sp);
        }
    }
    flags.hidden = !flags.children.length;
    $('geoAnswerPanel').hidden = false;
    $('geoSols').hidden = true;
}

function renderCoordSolution(result, pts, tri) {
    const fmt = formatter();
    const pseudo = { qty: result.qty, rules: [] };
    const steps = result.steps.map((st) => renderStep(pseudo, st.rule, st.res, result.values, fmt, sym));
    const given = pts.map((p) => {
        const x = p.name + '\\left(' + fmt.value(p.x).tex + coordTex() + fmt.value(p.y).tex + '\\right)';
        return { tex: x, text: texToText(x) };
    });
    const answer = Object.entries(result.values).map(([id, v]) => {
        const q = result.qty[id];
        const f = q.kind === 'angle' ? fmt.angle(v) : fmt.quantity(v, q.kind);
        const s0 = symbols('@' + id, pseudo, sym);
        const x = s0 + (f.approx ? ' \\approx ' : ' = ') + f.tex;
        return { tex: x, text: texToText(x) };
    });
    void tri;
    const sol = { given, find: [], steps, answer };
    state.solution = sol;
    fillSolutionDom(sol);
}
function fillSolutionDom(sol) {
    $('geoMethods').hidden = true;
    const put = (box, list, sepr) => {
        box.innerHTML = '';
        list.forEach((x, i) => {
            const sp = document.createElement('span');
            tex(sp, x.tex);
            box.appendChild(sp);
            if (i < list.length - 1) box.appendChild(document.createTextNode(sepr));
        });
    };
    put($('geoGiven'), sol.given, listSep());
    $('geoFind').hidden = !sol.find.length;
    put($('geoFindList'), sol.find, listSep());
    const ol = $('geoSteps');
    ol.innerHTML = '';
    for (const st of sol.steps) {
        const li = document.createElement('li');
        const nm = document.createElement('div');
        nm.className = 'geo-step-name';
        nm.textContent = ruleName(st.name);
        li.appendChild(nm);
        if (st.general) { const gd = document.createElement('div'); gd.className = 'geo-step-gen'; tex(gd, st.general); li.appendChild(gd); }
        for (const ln of st.lines) { const d = document.createElement('div'); d.className = 'geo-step-line'; tex(d, ln); li.appendChild(d); }
        ol.appendChild(li);
    }
    put($('geoAnswer'), sol.answer, listSep());
    $('geoSolutionPanel').hidden = false;
}

function refreshVectors(fromUser) {
    const fmt = formatter();
    const read = (v) => {
        const o = {};
        let bad = 0;
        for (const c of state.vec.dim3 ? ['x', 'y', 'z'] : ['x', 'y']) {
            const inp = $('geoV' + v + c);
            const r = parseCoord(state.vec[v][c]);
            if (inp) setFieldError(inp, r.error ? t(r.big ? 'field_err_big' : 'field_err') : null);
            if (r.error) { if (fromUser) refused(r.big ? 'coord_too_big' : 'coord_unreadable', { mode: 'vectors' }, state.vec[v][c]); bad++; }
            if (r.v) o[c] = r.v;
        }
        return { o, bad, ok: o.x && o.y && (!state.vec.dim3 || o.z) };
    };
    const a = read('a'); const b = read('b');
    state.coordRes = null;
    if (!a.ok || !b.ok) {
        showStatus(a.bad || b.bad ? [] : [{ cls: '', text: t('status_empty') }]);
        renderEmpty();
        saveHash();
        return;
    }
    const result = solveVectors(a.o, b.o, { fmt, sepTex: coordTex() });
    if (result.status !== 'ok') {
        if (fromUser) refused('contradiction', { mode: 'vectors', why: result.reason.key }, ['a', 'b'].map((v) => ['x', 'y', 'z'].map((c) => state.vec[v][c] || '').join(':')).join(';'));
        showStatus([{ cls: 'bad', text: fill(t('status_contradiction'), { why: (CFG.why || {})[result.reason.key] || result.reason.key }) }]);
        renderEmpty();
        saveHash();
        return;
    }
    showStatus([]);
    state.coordRes = { ok: true, a: a.o, b: b.o, result };
    // Таблица
    const body = $('geoTableBody');
    body.innerHTML = '';
    const vq = (CFG.qty || {}).vectors || {};
    for (const [id, v] of Object.entries(result.values)) {
        const q = result.qty[id];
        const f = q.kind === 'angle' ? fmt.angle(v) : fmt.value(v);
        const exact = f.exact;
        const tr = document.createElement('tr');
        tr.innerHTML = '<td><div class="geo-row" style="cursor:default"><span class="n"><b>' + richHtml(symbolText({ qty: result.qty }, id, sym)) + '</b>' + esc(vq[id] || id) + '</span><span class="v">'
            + esc((f.approx && !exact ? '≈ ' : '') + f.text) + (exact ? '<small>≈ ' + esc(f.dec) + '</small>' : '') + '</span></div></td>';
        body.appendChild(tr);
    }
    $('geoTable').hidden = false;
    const objs = $('geoObjs');
    objs.innerHTML = '';
    for (const o of result.objects) {
        const d = document.createElement('div');
        d.className = 'geo-obj';
        const n = document.createElement('span');
        n.className = 'n';
        n.textContent = t(o.name === 'a + b' ? 'obj_vector_sum' : 'obj_vector_diff');
        const v = document.createElement('span');
        tex(v, (o.name === 'a + b' ? '\\vec a + \\vec b' : '\\vec a - \\vec b') + ' = \\left(' + o.coords.map((x) => fmt.value(x).tex).join(coordTex()) + '\\right)');
        d.appendChild(n);
        d.appendChild(v);
        objs.appendChild(d);
    }
    objs.hidden = false;
    const flags = $('geoFlags');
    flags.innerHTML = '<span class="geo-flag">' + esc(t(result.collinear ? 'flag_collinear' : result.perpendicular ? 'flag_perpendicular' : 'flag_neither')) + '</span>';
    flags.hidden = false;
    $('geoAnswerPanel').hidden = false;
    $('geoSols').hidden = true;
    if (result.dim === 2) renderCoordDrawing();
    else {
        $('geoCanvas').innerHTML = '<div class="geo-empty">' + esc(readerText(t('dim3'), NOTATION)) + '</div>';
        drawingTools(false);
        $('geoCaption').textContent = '';
    }
    const pseudo = { qty: result.qty, rules: [] };
    const steps = result.steps.map((st) => renderStep(pseudo, st.rule, st.res, result.values, fmt, sym));
    const vecTex = (o) => '\\left(' + ['x', 'y', 'z'].filter((c) => o[c]).map((c) => fmt.value(o[c]).tex).join(coordTex()) + '\\right)';
    const given = [{ tex: '\\vec a = ' + vecTex(a.o) }, { tex: '\\vec b = ' + vecTex(b.o) }].map((x) => Object.assign(x, { text: texToText(x.tex) }));
    const answer = ['la', 'lb', 'dot', 'phi'].map((id) => {
        const v = result.values[id];
        const f = result.qty[id].kind === 'angle' ? fmt.angle(v) : fmt.value(v);
        const x = symbols('@' + id, pseudo, sym) + (f.approx ? ' \\approx ' : ' = ') + f.tex;
        return { tex: x, text: texToText(x) };
    });
    const sol = { given, find: [], steps, answer };
    state.solution = sol;
    fillSolutionDom(sol);
    saveHash();
    if (fromUser) {
        stage('solved', { fig: 'vectors' });
        resultShown({ mode: 'vectors', fig: 'vectors', dim: result.dim }, JSON.stringify(['vectors', state.vec]));
        recentLater();
    }
}

/* ── адресная строка ──────────────────────────────────────────────────────── */
function saveHash() {
    // Адрес вкладки «Построения» пишет её модуль (#c=…).
    if (state.tab === 'construct') return;
    const q = new URLSearchParams();
    q.set('f', state.fig);
    if (state.variant && state.variant !== 'general') q.set('v', state.variant);
    const meta = figureMeta(state.fig);
    if (meta && meta.n) q.set('n', String(state.n));
    if (state.fig === 'coords') q.set('p', state.points.map((p) => p.name + ':' + p.x + ':' + p.y).join(';'));
    else if (state.fig === 'vectors') q.set('p', ['a', 'b'].map((v) => ['x', 'y', 'z'].map((c) => state.vec[v][c] || '').join(':')).join(';') + (state.vec.dim3 ? ';3' : ''));
    else {
        const ins = Object.entries(state.inputs).filter(([, v]) => String(v || '').trim());
        if (ins.length) q.set('i', ins.map(([k, v]) => k + ':' + v).join(';'));
    }
    if (state.targets && state.targets.length) q.set('t', state.targets.join(','));
    if (state.method) q.set('m', state.method);
    if (state.sol) q.set('s', String(state.sol));
    const ov = Object.keys(state.overlays).filter((k) => state.overlays[k]);
    if (ov.length) q.set('o', ov.join(','));
    try { history.replaceState(null, '', '#' + q.toString()); } catch (_) { /* песочница */ }
}
function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    const f = q.get('f');
    if (!f || !figureMeta(f)) return false;
    if (f === 'coords' && q.get('p')) {
        state.points = q.get('p').split(';').map((s) => { const [name, x, y] = s.split(':'); return { name: name || 'A', x: x || '', y: y || '' }; }).slice(0, 8);
    }
    if (f === 'vectors' && q.get('p')) {
        const parts = q.get('p').split(';');
        const [ax, ay, az] = (parts[0] || '').split(':');
        const [bx, by, bz] = (parts[1] || '').split(':');
        state.vec = { a: { x: ax || '', y: ay || '', z: az || '' }, b: { x: bx || '', y: by || '', z: bz || '' }, dim3: parts[2] === '3' };
    }
    const inputs = {};
    for (const pair of (q.get('i') || '').split(';')) {
        const k = pair.indexOf(':');
        if (k > 0) inputs[pair.slice(0, k)] = pair.slice(k + 1);
    }
    state.inputs = inputs;
    selectFigure(f, { variant: q.get('v') || undefined, n: q.get('n') || undefined, keepInputs: true, scroll: false, auto: true });
    // Слои — после выбора фигуры: selectFigure() сбрасывает их для новой фигуры.
    state.overlays = {};
    (q.get('o') || '').split(',').filter(Boolean).forEach((k) => { state.overlays[k] = true; });
    state.targets = q.get('t') ? q.get('t').split(',') : null;
    state.method = q.get('m') || null;
    state.sol = Number(q.get('s')) || 0;
    buildOverlays();
    refresh(false);
    return true;
}

/* ── выдача: копия, PDF, печать, ссылка ───────────────────────────────────── */
function solutionPlain() {
    const sol = state.solution;
    if (!sol) return '';
    const head = figTitle(state.fig, state.variant, 0, false);
    const L = { given: t('given'), find: t('find'), solution: t('solution'), answer: t('answer'), labelSep: labelSep(), listSep: listSep(), rule: (n) => (n === 'numeric' ? t('numeric_step') : ruleName(n)) };
    return head + '\n' + solutionText(sol, L);
}
async function copySolution() {
    // Буфер обмена — простой текст: индексы без «_» (hₐ, hc).
    const plain = subPlain(solutionPlain());
    if (!plain) return;
    const text = isolateMath(plain);
    try {
        if (window.__copyText) await window.__copyText(text); else await navigator.clipboard.writeText(text);
        if (window.__toast) window.__toast(t('copied'), 'success');
        ae('copy', { fig: state.fig, len: text.length });
    } catch (_) {
        if (window.__toast) window.__toast(t('copy_failed'), 'error');
        refusedNow('copy_failed', { fig: state.fig });
    }
}
async function share() {
    saveHash();
    const url = location.href;
    if (navigator.share) {
        try { await navigator.share({ title: document.title, url }); ae('share', { via: 'native', fig: state.fig }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try {
        await navigator.clipboard.writeText(url);
        if (window.__toast) window.__toast(t('link_copied'), 'success');
        ae('share', { via: 'clipboard', fig: state.fig });
    } catch (_) { if (window.__toast) window.__toast(t('copy_failed'), 'error'); }
}

/** Чертёж для выдачи: светлый лист, конкретные цвета. */
function exportSvg(w = 900, h = 600) {
    if (isCoords()) {
        const r0 = state.coordRes;
        if (!r0) return null;
        const fmt = formatter();
        const d = state.fig === 'coords' ? drawPoints(r0.pts, r0.result, { fmt, sep: coordSep() }) : drawVectors(r0.a, r0.b, r0.result, { fmt, label: (id) => symbolText({ qty: r0.result.qty }, id, sym) });
        return Object.assign(d.sheet.render({ width: w, height: h, style: EXPORT_CSS, background: '#fff', bounds: d.bounds }), { sheet: d.sheet });
    }
    const res = state.res;
    if (!res || !res.solutions.length) return null;
    const fig = state.figObj;
    const s = res.solutions[state.sol] || res.solutions[0];
    const nums = numbers(s.values);
    if (!Object.keys(fig.qty).every((id) => nums[id] != null)) return null;
    const d = drawFigure(state.fig, nums, drawOptions(fig, s, { export: true, draggable: false }));
    return Object.assign(d.sheet.render({ width: w, height: h, style: EXPORT_CSS, background: '#fff' }), { sheet: d.sheet });
}
function fileBase() {
    const base = (figName(state.fig) || 'geometry').toLowerCase();
    try { if (window.__outputName) return window.__outputName.sanitize(base); } catch (_) { /* примитив не загрузился */ }
    return 'geometry';
}
function fileName(ext) {
    try { if (window.__outputName) return window.__outputName.join(fileBase(), ext); } catch (_) { /* ниже */ }
    return 'geometry.' + ext;
}
async function downloadPng() {
    const r = exportSvg();
    if (!r) return;
    try {
        const blob = await svgToPng(r.svg, r.layout.width, r.layout.height, 2);
        const saved = await window.__downloadFile(blob, fileName('png'));
        ae('export', { kind: 'png', fig: state.fig, ok: saved === false ? 0 : 1 });
        if (saved !== false) { try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'png', fig: state.fig }); } catch (_) { /* телеметрия */ } }
    } catch (e) { report(e, 'png', t('png_failed')); }
}
function downloadSvg() {
    const r = exportSvg();
    if (!r) return;
    window.__downloadFile(new Blob([r.svg], { type: 'image/svg+xml' }), fileName('svg'));
    ae('export', { kind: 'svg', fig: state.fig });
}
async function copyImage() {
    const r = exportSvg();
    if (!r) return;
    try {
        // Safari отдаёт буфер обмена только в стеке нажатия: картинка уходит обещанием внутри
        // ClipboardItem, а не после await (иначе на любом iPhone — NotAllowedError и «не удалось»).
        const png = svgToPng(r.svg, r.layout.width, r.layout.height, 2);
        try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
        } catch (e1) {
            // Старый Chromium не принимает обещание в ClipboardItem — тогда готовой картинкой.
            if (!e1 || e1.name !== 'TypeError') throw e1;
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': await png })]);
        }
        if (window.__toast) window.__toast(t('img_copied'), 'success');
        ae('export', { kind: 'clipboard', fig: state.fig });
    } catch (e) {
        if (window.__toast) window.__toast(t('img_copy_failed'), 'error');
        refusedNow('img_copy_failed', { fig: state.fig, err: String((e && e.name) || '').slice(0, 30) });
    }
}
async function printLifeSize() {
    const r = exportSvg();
    if (!r) return;
    const btn = $('geoLife');
    btn.disabled = true;
    try {
        const mm = (LENGTH[state.unit][0] / LENGTH[state.unit][1]) * 1000;
        const sol = state.solution;
        const lines = [];
        if (sol) {
            lines.push(subPlain(t('given') + labelSep() + sol.given.map((g) => g.text).join(listSep())));
            lines.push(subPlain(t('answer') + labelSep() + sol.answer.map((a) => a.text).join(listSep())));
        }
        const out = await lifePdf(r.sheet, {
            mmPerUnit: mm, title: fill(t('life_title'), { name: figName(state.fig) }), lines, fmt: formatter(), paper: NOTATION.paper,
            strings: { scale1: t('life_scale'), scaleK: t('life_scaled'), calibrated: t('life_calibrated'), note: t('life_note') },
        });
        window.__downloadFile(out.blob, fileName('pdf'));
        ae('export', { kind: 'life_pdf', fig: state.fig, k: out.k, cal: out.cal !== 1 ? 1 : 0, paper: NOTATION.paper });
        try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'life_pdf', fig: state.fig }); } catch (_) { /* телеметрия */ }
    } catch (e) {
        report(e, 'life_pdf', t('life_error'));
    } finally { btn.disabled = false; }
}

/**
 * KaTeX рисует знак корня SVG-картинкой шириной 400em с «slice»: видна только
 * левая часть, остальное обрезает родитель. html2canvas (PDF) этого не умеет —
 * сжимает всю картинку в видимую рамку, и от «√3» остаётся черта «‾3». В копии
 * для отчёта картинке задаётся ровно видимый кусок: viewBox по видимой ширине,
 * размеры в пикселях, без «slice».
 */
function emToPx(live, copy) {
    const a = live.querySelectorAll('.katex svg');
    const b = copy.querySelectorAll('.katex svg');
    a.forEach((svg, i) => {
        const c = b[i];
        if (!c) return;
        const box = (svg.parentElement || svg).getBoundingClientRect();
        const own = svg.getBoundingClientRect();
        const w = Math.min(box.width, own.width);
        const h = own.height;
        const vb = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
        if (!(w > 0 && h > 0) || vb.length !== 4 || !(vb[3] > 0)) return;
        const par = svg.getAttribute('preserveAspectRatio') || '';
        const visW = /slice/.test(par) ? Math.min(vb[2], (w / h) * vb[3]) : vb[2];
        c.setAttribute('viewBox', vb[0] + ' ' + vb[1] + ' ' + visW.toFixed(1) + ' ' + vb[3]);
        c.setAttribute('preserveAspectRatio', 'none');
        c.setAttribute('width', w.toFixed(2) + 'px');
        c.setAttribute('height', h.toFixed(2) + 'px');
        c.style.width = w.toFixed(2) + 'px';
        c.style.height = h.toFixed(2) + 'px';
    });
}

function buildReport() {
    const root = document.createElement('div');
    root.className = 'lg-app geo geo-report';
    root.style.cssText = 'padding:0;background:#fff;color:#111827;max-width:none';
    const h = document.createElement('h2');
    h.style.cssText = 'font-size:20px;margin:0 0 8px';
    h.textContent = figName(state.fig);
    root.appendChild(h);
    const r = exportSvg(760, 480);
    if (r) {
        const img = document.createElement('img');
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(r.svg);
        img.style.cssText = 'width:100%;max-width:640px;display:block;border:1px solid #d6dde8;border-radius:10px;margin:0 0 12px';
        root.appendChild(img);
    }
    const sol = $('geoSolutionPanel').cloneNode(true);
    emToPx($('geoSolutionPanel'), sol);
    sol.removeAttribute('id');
    sol.hidden = false;
    sol.querySelectorAll('.tc-actions,.geo-methods,[id]').forEach((el) => { if (el.classList.contains('tc-actions') || el.classList.contains('geo-methods')) el.remove(); else el.removeAttribute('id'); });
    root.appendChild(sol);
    const tbl = $('geoAnswerPanel').cloneNode(true);
    tbl.removeAttribute('id');
    tbl.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    // В отчёте — все величины: свёрнутые строки раскрыты, кнопки «Ещё» нет.
    tbl.querySelectorAll('tr[hidden]').forEach((tr) => { tr.hidden = false; });
    tbl.querySelectorAll('.geo-vals-more').forEach((el) => el.closest('tr').remove());
    tbl.querySelectorAll('.tc-hint').forEach((el) => el.remove());
    root.appendChild(tbl);
    return root;
}
function savePdf() {
    if ($('geoSolutionPanel').hidden || !engineReady('__generatePDF', t('pdf_error'))) return;
    const btn = $('geoPdf');
    btn.disabled = true;
    window.__generatePDF({
        element: buildReport(), title: t('pdf_title'), filename: fileName('pdf'),
        strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((res) => {
        btn.disabled = false;
        ae('export', { kind: 'pdf', ok: !!(res && res.ok), fig: state.fig });
        if (res && res.ok) { try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'pdf', fig: state.fig }); } catch (_) { /* телеметрия */ } }
    }, () => { btn.disabled = false; });
}

/* ── тренажёр ─────────────────────────────────────────────────────────────── */
function seed() { try { return crypto.getRandomValues(new Uint32Array(1))[0]; } catch (_) { return Math.floor(Math.random() * 4294967295); } }

/** Задача → {text, want (V), problem} — ответ считает решатель. */
function problemFacts(p) {
    const fig = makeFigure(p.fig, { variant: p.variant, n: p.n });
    const givens = {};
    for (const [id, raw] of Object.entries(p.givens)) {
        const q = fig.qty[id];
        const r = q.kind === 'angle' ? parseAngle(raw) : parseMeasure(raw, q.kind, state.unit);
        if (r.error) return null;
        givens[id] = { v: r.v };
    }
    for (const [id, v] of Object.entries(fig.presets || {})) givens[id] = { v };
    const res = solve(fig, givens, { pi: piValue() });
    if (res.status !== 'ok' || !res.solutions[0].values[p.target]) return null;
    const fmt = formatter();
    const gtxt = Object.keys(p.givens).map((id) => {
        const q = fig.qty[id];
        return symbolText(fig, id, sym) + ' = ' + fmt.quantity(givens[id].v, q.kind).text;
    }).join(listSep());
    const name = figTitle(p.fig, p.variant, p.n, true);
    const text = fill(t('practice_task'), { fig: name, given: gtxt, find: fill(t('sym_name'), { sym: symbolText(fig, p.target, sym), name: qName(p.fig, p.target, p.variant || null) }) });
    return { text, want: res.solutions[0].values[p.target], fig, res, givens };
}
function newPractice() {
    const R = rng(seed());
    let facts = null;
    let p = null;
    for (let i = 0; i < 12 && !facts; i++) { p = makeProblem(state.fig, R); facts = problemFacts(p); }
    if (!facts) return;
    state.practice = { p, facts };
    const task = $('geoTask');
    setText(task, facts.text);
    task.hidden = false;
    $('geoPracticeAnswer').hidden = false;
    $('geoPracticeAnswer').value = '';
    $('geoPracticeCheck').hidden = false;
    $('geoPracticeShow').hidden = false;
    $('geoPracticeMsg').hidden = true;
    ae('practice_new', { fig: p.fig });
}
function checkPractice() {
    const pr = state.practice;
    if (!pr) return;
    const msg = $('geoPracticeMsg');
    const r = checkAnswer($('geoPracticeAnswer').value, pr.facts.want);
    msg.hidden = false;
    if (r.empty || r.parse) {
        msg.className = 'geo-practice-msg bad';
        msg.textContent = t('practice_empty');
        if (r.parse) refusedNow('practice_unreadable', { fig: pr.p.fig }, $('geoPracticeAnswer').value);
        return;
    }
    msg.className = 'geo-practice-msg ' + (r.ok ? 'good' : 'bad');
    msg.textContent = r.ok ? t('practice_right') : r.close ? t('practice_close') : t('practice_wrong');
    ae('practice_check', { ok: r.ok ? 1 : 0, fig: pr.p.fig });
}
function showPractice() {
    const pr = state.practice;
    if (!pr) return;
    const p = pr.p;
    const meta = figureMeta(p.fig);
    selectGroup(meta.group, { keepFig: true });
    state.inputs = Object.assign({}, p.givens);
    selectFigure(p.fig, { variant: p.variant, n: p.n, keepInputs: true });
    state.targets = [p.target];
    refresh(true);
    ae('practice_show', { fig: p.fig });
}

function makeWorksheet() {
    if (!engineReady('__generatePDF', t('ws_error'))) return;
    const count = Math.max(1, Math.min(12, parseInt($('geoWsCount').value, 10) || 6));
    const variants = Math.max(1, Math.min(4, parseInt($('geoWsVariants').value, 10) || 2));
    const base = seed();
    const fmt = formatter();
    const sheet = document.createElement('div');
    sheet.style.cssText = 'color:#1e293b;font-family:inherit;background:#fff';
    const keys = [];
    for (let vi = 0; vi < variants; vi++) {
        const R = rng(base + vi * 7919);
        const list = [];
        const seen = new Set();
        for (let j = 0; list.length < count && j < count * 30; j++) {
            const p = makeProblem(state.fig, R);
            const key = JSON.stringify(p);
            if (seen.has(key)) continue;
            seen.add(key);
            const f = problemFacts(p);
            if (f) list.push({ p, f });
        }
        const page = document.createElement('section');
        page.style.cssText = 'padding:0 0 8px;' + (vi ? 'page-break-before:always;break-before:page;' : '');
        page.innerHTML = '<div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;border-bottom:2px solid #1e293b;padding-bottom:6px;margin-bottom:10px">'
            + '<b style="font-size:18px">' + esc(t('ws_heading')) + '</b><span style="font-size:14px">' + esc(fill(t('ws_variant'), { n: vi + 1 })) + '</span></div>'
            + '<p style="font-size:13px;margin:0 0 6px">' + esc(t('ws_name') + labelSep()) + '______________________________</p>'
            + '<p style="font-size:14px;font-weight:700;margin:10px 0">' + esc(t('ws_task')) + '</p>';
        const grid = document.createElement('div');
        grid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:14px';
        list.forEach(({ p, f }, i) => {
            const cell = document.createElement('div');
            cell.style.cssText = 'break-inside:avoid;border:1px solid #cbd5e1;border-radius:8px;padding:8px';
            const tx = document.createElement('p');
            tx.style.cssText = 'font-size:13px;margin:0 0 6px;line-height:1.45';
            putRich(tx, (i + 1) + '. ' + f.text);
            cell.appendChild(tx);
            const nums = numbers(f.res.solutions[0].values);
            const lab = (id) => {
                const s0 = symbolText(f.fig, id, sym);
                if (p.givens[id] != null) {
                    const q = f.fig.qty[id];
                    return s0 + ' = ' + (q.kind === 'angle' ? fmt.angle(f.givens[id].v).text : fmt.value(f.givens[id].v).text);
                }
                return id === p.target ? s0 + ' = ?' : s0;
            };
            const d = drawFigure(p.fig, nums, { label: lab, highlight: new Set([p.target]), given: new Set(Object.keys(p.givens)), overlays: {}, n: p.n, fmt, showAngles: true });
            if (d) {
                const r = d.sheet.render({ width: 340, height: 210, style: EXPORT_CSS, background: '#fff' });
                const img = document.createElement('img');
                img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(r.svg);
                img.style.cssText = 'width:100%;display:block';
                cell.appendChild(img);
            }
            grid.appendChild(cell);
        });
        page.appendChild(grid);
        sheet.appendChild(page);
        keys.push(list.map(({ p, f }) => symbolText(f.fig, p.target, sym) + ' = ' + (fmt.value(f.want).exact ? fmt.value(f.want).text + ' ≈ ' + fmt.value(f.want).dec : fmt.quantity(f.want, f.fig.qty[p.target].kind).text)));
    }
    const key = document.createElement('section');
    key.style.cssText = 'page-break-before:always;break-before:page';
    key.innerHTML = '<b style="font-size:18px;display:block;border-bottom:2px solid #1e293b;padding-bottom:6px;margin-bottom:10px">' + esc(t('ws_key')) + '</b>';
    keys.forEach((answers, vi) => {
        const hh = document.createElement('p');
        hh.style.cssText = 'font-weight:700;margin:10px 0 4px;font-size:14px';
        hh.textContent = fill(t('ws_variant'), { n: vi + 1 });
        key.appendChild(hh);
        const ol = document.createElement('ol');
        ol.style.cssText = 'columns:2;column-gap:28px;margin:0;padding-inline-start:22px;font-size:13px;line-height:1.7';
        answers.forEach((a) => { const li = document.createElement('li'); putRich(li, a); ol.appendChild(li); });
        key.appendChild(ol);
    });
    sheet.appendChild(key);
    const btn = $('geoWsMake');
    btn.disabled = true;
    window.__generatePDF({
        element: sheet, filename: fileName('pdf'),
        strings: { libError: t('ws_error'), genError: t('ws_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => { btn.disabled = false; ae('worksheet', { ok: !!(r && r.ok), fig: state.fig, count, variants });
        if (r && r.ok) { try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'worksheet', fig: state.fig, count, variants }); } catch (_) { /* телеметрия */ } } }, () => { btn.disabled = false; });
}

/* ── настройки ────────────────────────────────────────────────────────────── */
function segSet(boxId, attr, value) {
    document.querySelectorAll('#' + boxId + ' [' + attr + ']').forEach((b) => {
        const on = b.getAttribute(attr) === value;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
}
/**
 * Строка под заголовком ящика «Единицы и точность»: единица и шаг округления
 * числом языка («см · 0,01»). Числом, а не словами «знаков после запятой: 2»:
 * так она помещается в узкую колонку на любом языке и не просит формы
 * множественного числа.
 */
function settingsSub() {
    const u = (CFG.units || {})[state.unit] || state.unit;
    const d = Math.max(0, Number(state.digits) || 0);
    $('geoSettingsSub').textContent = fill(t('settings_sub'), { unit: u, step: formatter().decimal(Math.pow(10, -d), d) });
}
function saveSetting(key, value) {
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, key, value); } catch (_) { /* настройки — удобство */ }
}
function loadSettings() {
    const ld = (k, d) => { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } };
    const u = ld('unit', 'cm');
    if (LENGTH[u]) state.unit = u;
    const d = Number(ld('digits', 2));
    if ([0, 1, 2, 3, 4, 6].includes(d)) state.digits = d;
    const a = ld('angle', 'deg');
    if (['deg', 'dms', 'rad'].includes(a)) state.angle = a;
    const p = ld('pi', 'symbol');
    if (['symbol', '3.14', 'decimal'].includes(p)) state.pi = p;
    const l = ld('labels', 'values');
    if (['values', 'letters'].includes(l)) state.labels = l;
    $('geoUnit').value = state.unit;
    $('geoDigits').value = String(state.digits);
    segSet('geoAngle', 'data-geo-angle', state.angle);
    segSet('geoPi', 'data-geo-pi', state.pi);
    segSet('geoLabels', 'data-geo-labels', state.labels);
    settingsSub();
}

/* ── вкладки режима ───────────────────────────────────────────────────────── */
/**
 * Вкладка «Построения» подключается модулем construct/: он снимает hidden с
 * #geoTabConstruct и слушает событие `geo:tab` (detail: 'construct').
 */
/**
 * Переключатель режимов виден, только когда режимов больше одного: одинокая
 * вкладка «Фигуры» — пустая строка над прибором. Модулю построений достаточно
 * снять hidden с #geoTabConstruct — наблюдатель покажет переключатель сам.
 */
function syncModes() {
    const box = $('geoModes');
    if (!box) return;
    const visible = Array.from(box.querySelectorAll('[data-geo-tab]')).filter((b) => !b.hidden).length;
    box.hidden = visible < 2;
}
function watchModes() {
    syncModes();
    try {
        const mo = new MutationObserver(syncModes);
        document.querySelectorAll('[data-geo-tab]').forEach((b) => mo.observe(b, { attributes: true, attributeFilter: ['hidden'] }));
    } catch (_) { /* старый браузер: переключатель останется как есть */ }
}

/**
 * Модуль построений грузится при первом открытии вкладки: калькулятору фигур
 * его код не нужен. Относительный import() наследует версию из пути модуля
 * (/jsv/…), как и статические импорты.
 */
let constructP = null;
function loadConstruct(hash) {
    if (!constructP) {
        constructP = import('./construct/index.js').then((m) => {
            m.mount({ state, openTab }, { hash: hash || null });
            return m;
        }).catch((e) => {
            constructP = null;
            console.error('[geometry-calculator] construct:', e);
            if (window.__toast) window.__toast(t('construct_load_failed'), 'error');
            refusedNow('construct_load_failed', { msg: String(e && e.message).slice(0, 60) });
            throw e;
        });
    }
    return constructP;
}

function openTab(tab, o = {}) {
    state.tab = tab;
    document.querySelectorAll('[data-geo-tab]').forEach((b) => {
        const on = b.getAttribute('data-geo-tab') === tab;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    $('geoShapes').hidden = tab !== 'shapes';
    $('geoConstruct').hidden = tab !== 'construct';
    try { document.dispatchEvent(new CustomEvent('geo:tab', { detail: tab })); } catch (_) { /* старый браузер */ }
    if (tab === 'construct') loadConstruct(o.hash).catch(() => { /* сказано тостом */ });
    else saveHash();
}

/* ── запуск ───────────────────────────────────────────────────────────────── */
function init() {
    if (!$('geoApp')) return;
    loadSettings();
    // Подписи, которые отрисовал сервер со знаком языка («π ≈ 3,14»), — знаком
    // страны читателя (es-MX: «π ≈ 3.14»): сервер страны не знает.
    if (NOTATION.region) document.querySelectorAll('#geoPi [data-geo-pi]').forEach((b) => { b.textContent = readerText(b.textContent, NOTATION); });
    document.querySelectorAll('[data-geo-tab]').forEach((b) => b.addEventListener('click', () => openTab(b.getAttribute('data-geo-tab'))));
    watchModes();
    document.querySelectorAll('[data-geo-group]').forEach((b) => b.addEventListener('click', () => selectGroup(b.getAttribute('data-geo-group'))));
    document.querySelectorAll('[data-geo-fig]').forEach((b) => b.addEventListener('click', () => selectFigure(b.getAttribute('data-geo-fig'))));
    $('geoVariant').addEventListener('click', (e) => {
        const b = e.target.closest('[data-geo-variant]');
        if (!b) return;
        state.variant = b.dataset.geoVariant;
        // Предустановки варианта (γ = 90°) заменяют набранное в этих полях.
        buildVariant();
        buildFields();
        buildFormulas();
        refresh(true);
        ae('variant', { fig: state.fig, v: state.variant });
    });
    $('geoN').addEventListener('change', () => {
        const meta = figureMeta(state.fig);
        const n = Math.max(meta.n[0], Math.min(meta.n[1], Math.round(Number($('geoN').value) || meta.n[2])));
        state.n = n;
        $('geoN').value = String(n);
        buildFields();
        buildFormulas();
        refresh(true);
    });
    document.querySelectorAll('[data-geo-key]').forEach((b) => b.addEventListener('pointerdown', (e) => e.preventDefault()));
    document.querySelectorAll('[data-geo-key]').forEach((b) => b.addEventListener('click', () => {
        const f = lastField && document.body.contains(lastField) ? lastField : document.querySelector('#geoFields .geo-in:not(:disabled)');
        if (!f) return;
        const ins = b.getAttribute('data-geo-key');
        const a = f.selectionStart == null ? f.value.length : f.selectionStart;
        const z = f.selectionEnd == null ? a : f.selectionEnd;
        f.value = f.value.slice(0, a) + ins + f.value.slice(z);
        try { f.setSelectionRange(a + ins.length, a + ins.length); } catch (_) { /* поле без выделения */ }
        f.focus();
        storeInput(f);
        clearTimeout(typeTimer);
        typeTimer = setTimeout(() => refresh(true), 260);
    }));
    $('geoClear').addEventListener('click', () => {
        if (state.fig === 'coords') state.points = [{ name: 'A', x: '', y: '' }, { name: 'B', x: '', y: '' }];
        else if (state.fig === 'vectors') state.vec = { a: {}, b: {}, dim3: state.vec.dim3 };
        else state.inputs = {};
        state.targets = null;
        buildFields();
        refresh(true);
        ae('clear', { fig: state.fig });
    });
    $('geoUnit').addEventListener('change', () => { state.unit = $('geoUnit').value; saveSetting('unit', state.unit); settingsSub(); refresh(true); ae('settings', { k: 'unit', v: state.unit }); });
    $('geoDigits').addEventListener('change', () => { state.digits = Number($('geoDigits').value); saveSetting('digits', state.digits); settingsSub(); refresh(false); ae('settings', { k: 'digits', v: state.digits }); });
    document.querySelectorAll('[data-geo-angle]').forEach((b) => b.addEventListener('click', () => { state.angle = b.dataset.geoAngle; segSet('geoAngle', 'data-geo-angle', state.angle); saveSetting('angle', state.angle); refresh(false); ae('settings', { k: 'angle', v: state.angle }); }));
    document.querySelectorAll('[data-geo-pi]').forEach((b) => b.addEventListener('click', () => { state.pi = b.dataset.geoPi; segSet('geoPi', 'data-geo-pi', state.pi); saveSetting('pi', state.pi); refresh(false); ae('settings', { k: 'pi', v: state.pi }); }));
    document.querySelectorAll('[data-geo-labels]').forEach((b) => b.addEventListener('click', () => { state.labels = b.dataset.geoLabels; segSet('geoLabels', 'data-geo-labels', state.labels); saveSetting('labels', state.labels); renderDrawing(); ae('settings', { k: 'labels', v: state.labels }); }));
    $('geoOverlays').addEventListener('click', (e) => {
        const b = e.target.closest('[data-geo-overlay]');
        if (!b) return;
        const k = b.dataset.geoOverlay;
        state.overlays[k] = !state.overlays[k];
        b.classList.toggle('on', state.overlays[k]);
        b.setAttribute('aria-pressed', state.overlays[k] ? 'true' : 'false');
        renderDrawing();
        saveHash();
        ae('overlay', { k, on: state.overlays[k] ? 1 : 0 });
    });
    $('geoSols').addEventListener('click', (e) => {
        const b = e.target.closest('[data-geo-sol]');
        if (!b) return;
        state.sol = Number(b.dataset.geoSol);
        renderAll();
        ae('solution_switch', { i: state.sol });
    });
    $('geoTableBody').addEventListener('click', (e) => {
        if (e.target.closest('[data-geo-allvals]') && state.res) {
            state.allValues = !state.allValues;
            renderTable(state.figObj, state.res.solutions[state.sol], state.res);
            const again = document.querySelector('[data-geo-allvals]');
            if (again) again.focus();
            ae('all_values', { on: state.allValues ? 1 : 0 });
            return;
        }
        const b = e.target.closest('[data-geo-target]');
        if (!b || b.disabled || !state.res) return;
        const id = b.dataset.geoTarget;
        const fig = state.figObj;
        const s = state.res.solutions[state.sol];
        let cur = currentTargets(fig, s);
        cur = cur.includes(id) ? cur.filter((x) => x !== id) : cur.concat([id]);
        state.targets = cur.length ? cur : [id];
        state.method = null;
        renderTable(fig, s, state.res);
        renderDrawing();
        renderSolution(fig, s, state.res);
        saveHash();
        ae('target', { fig: state.fig, id });
    });
    $('geoMethods').addEventListener('click', (e) => {
        const b = e.target.closest('[data-geo-method]');
        if (!b || !state.res) return;
        state.method = b.dataset.geoMethod;
        const apply = () => renderSolution(state.figObj, state.res.solutions[state.sol], state.res);
        if (window.__viewTransition) window.__viewTransition(apply); else apply();
        saveHash();
        ae('method', { fig: state.fig, m: state.method });
    });
    $('geoCopy').addEventListener('click', copySolution);
    $('geoShare').addEventListener('click', share);
    $('geoPdf').addEventListener('click', savePdf);
    $('geoPrint').addEventListener('click', () => { ae('export', { kind: 'print', fig: state.fig }); window.print(); });
    $('geoPng').addEventListener('click', downloadPng);
    $('geoSvg').addEventListener('click', downloadSvg);
    $('geoCopyImg').addEventListener('click', copyImage);
    $('geoLife').addEventListener('click', printLifeSize);
    $('geoToConstruct').addEventListener('click', () => {
        const sides = triangleSidesCm();
        if (!sides) return;
        openTab('construct');
        loadConstruct().then((m) => m.fromTriangle(sides)).catch(() => { /* сказано тостом */ });
        ae('to_construct', {});
    });
    $('geoPracticeNew').addEventListener('click', newPractice);
    $('geoPracticeCheck').addEventListener('click', checkPractice);
    $('geoPracticeAnswer').addEventListener('keydown', (e) => { if (e.key === 'Enter') checkPractice(); });
    $('geoPracticeShow').addEventListener('click', showPractice);
    $('geoWsMake').addEventListener('click', makeWorksheet);
    bindDrag();
    recentInit();

    // Ссылка на построение (#c=…): калькулятор фигур готовится как обычно,
    // но адрес не трогает, и сразу открывается вкладка «Построения».
    const startHash = String(location.hash || '');
    const toConstruct = /(^#|&)c=/.test(startHash);
    if (toConstruct) state.tab = 'construct';
    if (toConstruct || !loadHash()) {
        const s0 = ((CFG.samples || {}).triangle || [])[0];
        selectFigure('triangle', { scroll: false, auto: true });
        if (s0) applySample(s0, true);
    }
    if (toConstruct) openTab('construct', { hash: startHash });
    let raf = 0;
    let lastW = 0;
    window.addEventListener('resize', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
            const w = $('geoCanvas').clientWidth;
            if (Math.abs(w - lastW) > 4) { lastW = w; renderDrawing(); }
        });
    });
    // KaTeX мог приехать позже модуля — перерисовать формулы.
    if (!window.katex) {
        const s = document.querySelector('script[data-expect=katex]');
        if (s) s.addEventListener('load', () => { buildFormulas(); if (state.res || state.coordRes) refresh(false); });
    }
    // Опора для сторожей: состояние видно снаружи без чтения пикселей.
    window.__geoState = state;
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
